/**
 * discoveryService.js — Discovery feature data layer.
 *
 * IMPORTANT SCOPE: Discovery reports on THIRD-PARTY AI developer-tool
 * spend & activity (Claude Code, GitHub Copilot / GitHub Enterprise,
 * etc.) that the tenant's engineers consume *outside* this platform,
 * ingested via the connector -> staging -> bronze data pipeline
 * and read live from the tenant's BYO warehouse bronze schemas
 * (bronze__claude_code.*, bronze__github_enterprise.*, bronze__rally.*).
 *
 * This is NOT the same as AI Audit. AI Audit tracks in-product Chat-page
 * usage (LLM calls this platform itself makes to Bedrock from
 * tenant-api/routers/data_chat.py + manager_chat.py, written to
 * AIInteractionLog on every call). No AI-Audit signal is surfaced here:
 * the per-user token-licence panel went out with the chat surface, and its
 * /api/ai-quota/me endpoint no longer exists.
 *
 * NO SYNTHETIC FALLBACK. If the warehouse is not connected or a
 * particular bronze table is empty, the affected accessor returns
 * empty data and the page surfaces an empty state — never fake rows.
 *
 * Surface used by the Discovery pages:
 *
 *   • Roster        : listTeams, listMembers, listProjects, listStories,
 *                     resolveCurrentMember, listTeamsForMember
 *   • Quotas        : getMemberQuota, getTeamQuota
 *   • Usage         : getMemberUsage, getTeamUsage, getTeamMemberUsage,
 *                     getOrgUsage, getProjectUsage, getStoryUsage
 *   • Productivity  : getTeamProductivity
 *   • Budgets       : listBudgets (read-only — write moved to org-structure/allocations)
 *                     (the client-side alert-event helpers were removed
 *                      2026-08-19 — alerting lives in /budget-alerts/*)
 *   • Loader        : ensureFinanceDataLoaded({days}), useFinanceData(days)
 */
import { useEffect, useState } from 'react';
import { coalesceInFlight, trackedFetch } from './inflight';
import { logger } from '../utils/logger';

const USER_EMAIL = 'user_email';

// Every request in this module goes through `trackedFetch`, which bumps the
// in-flight epoch on any non-GET. That is what makes a read issued AFTER a
// write safe to coalesce: it can never be handed the answer to a read that
// started before the write. Shadowing `fetch` here means a new write site
// cannot forget (see inflight.js for the alert-rule bug this comes from).
const fetch = trackedFetch;

const API_BASE = '/api/tenant/api';

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function apiGet(path) {
  // Identical GETs issued concurrently by different components share one
  // request (never a cached result — see services/inflight.js). Measured on
  // /discovery/org: `me/org-scope` was fetched 4x and `tiers` 4x per page
  // load, each duplicate taking a slot the page's other calls queued behind.
  // Only GETs: `apiSend` handles the mutations and must never be shared.
  return coalesceInFlight(`GET ${path}`, async () => {
    const res = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
    if (!res.ok) {
      const err = new Error(`GET ${path} failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    return res.json();
  });
}
async function apiSend(method, path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method, headers: authHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.detail || `${method} ${path} failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.status === HTTP_NO_CONTENT ? null : res.json();
}

// ── Model price catalogue ─────────────────────────────────────────────
// USD per 1M tokens (Anthropic list price, checked 2026-10-27). Mirrors
// _MODEL_PRICES in services/tenant-api/routers/discovery.py.
const MODELS = [
  { key: 'claude-fable-5-1',  label: 'Claude Fable 5.1',  input: 10, cache_write_5m: 12.50, cache_write_1h: 20, cache_read: 0.25, output: 50 },
  { key: 'claude-fable-5',    label: 'Claude Fable 5',    input: 10, cache_write_5m: 12.50, cache_write_1h: 20, cache_read: 1.00, output: 50 },
  { key: 'claude-opus-5-5',   label: 'Claude Opus 5.5',   input: 4,  cache_write_5m: 5,     cache_write_1h: 8,  cache_read: 0.20, output: 20 },
  { key: 'claude-opus-5',     label: 'Claude Opus 5',     input: 5,  cache_write_5m: 6.25,  cache_write_1h: 10, cache_read: 0.50, output: 25 },
  { key: 'claude-opus-4-8',   label: 'Claude Opus 4.8',   input: 5,  cache_write_5m: 6.25,  cache_write_1h: 10, cache_read: 0.50, output: 25 },
  { key: 'claude-opus-4-7',   label: 'Claude Opus 4.7',   input: 5,  cache_write_5m: 6.25,  cache_write_1h: 10, cache_read: 0.50, output: 25 },
  { key: 'claude-opus-4-6',   label: 'Claude Opus 4.6',   input: 5,  cache_write_5m: 6.25,  cache_write_1h: 10, cache_read: 0.50, output: 25 },
  { key: 'claude-opus-4-5',   label: 'Claude Opus 4.5',   input: 5,  cache_write_5m: 6.25,  cache_write_1h: 10, cache_read: 0.50, output: 25 },
  { key: 'claude-opus-4-1',   label: 'Claude Opus 4.1',   input: 15, cache_write_5m: 18.75, cache_write_1h: 30, cache_read: 1.50, output: 75 },
  { key: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5', input: 2,  cache_write_5m: 2.50,  cache_write_1h: 4,  cache_read: 0.10, output: 10 },
  { key: 'claude-sonnet-5',   label: 'Claude Sonnet 5',   input: 2,  cache_write_5m: 2.50,  cache_write_1h: 4,  cache_read: 0.20, output: 10 },
  { key: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6', input: 3,  cache_write_5m: 3.75,  cache_write_1h: 6,  cache_read: 0.30, output: 15 },
  { key: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5', input: 3,  cache_write_5m: 3.75,  cache_write_1h: 6,  cache_read: 0.30, output: 15 },
  { key: 'claude-haiku-5-5',  label: 'Claude Haiku 5.5',  input: 0.10, cache_write_5m: 0.125, cache_write_1h: 0.20, cache_read: 0.01, output: 0.50 },
  { key: 'claude-haiku-4-5',  label: 'Claude Haiku 4.5',  input: 1,  cache_write_5m: 1.25,  cache_write_1h: 2,  cache_read: 0.10, output: 5 },
];
export const FINANCE_MODELS = MODELS;

// GitHub retired Premium Request Units on 2026-06-01 in favour of usage-priced
// AI Credits (allowance bundled into the seat; overage token-priced). Surfaces
// that show a Copilot free-tier / allowance burn-down switch basis by this
// CUTOVER DATE, not by whether any credits happen to exist yet. Mirrors
// GH_AI_CREDIT_CUTOVER in services/tenant-api/routers/ai_finance.py.
const GH_CUTOVER_YEAR = 2026;
const GH_CUTOVER_MONTH_INDEX = 5; // June (month is 0-based)
export const GH_AI_CREDIT_CUTOVER_MS = Date.UTC(GH_CUTOVER_YEAR, GH_CUTOVER_MONTH_INDEX, 1); // 2026-06-01
export function onGhCreditModel(ms = Date.now()) { return ms >= GH_AI_CREDIT_CUTOVER_MS; }

// ── Date helpers ──────────────────────────────────────────────────────
const HTTP_NO_CONTENT = 204;

// Append `qs` to a path when non-empty (lead is '?' or '&').
function withQuery(path, qs, lead = '?') {
  return qs ? `${path}${lead}${qs}` : path;
}

function isoDay(d) { return d.toISOString().slice(0, 10); }
function dateRange(days, anchor = new Date()) {
  const out = [];
  const end = new Date(anchor); end.setUTCHours(0, 0, 0, 0);
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(end); d.setUTCDate(end.getUTCDate() - i);
    out.push(isoDay(d));
  }
  return out;
}

// ── Series row helpers ────────────────────────────────────────────────
function emptyDailyRow(date) {
  return {
    date,
    interactions: 0,
    // cacheTokens (cache_creation + cache_read) is a subset of inputTokens —
    // uncached input is inputTokens - cacheTokens. Surfaced for the Org
    // AI Usage "Invoice" card's Claude Cache | Input | Output split.
    inputTokens: 0, cacheTokens: 0, outputTokens: 0, totalTokens: 0,
    cost: 0,
    perModel: {}, perModelCost: {},
    // Productivity primitives from claude_code__user_activity
    // (also exposed by /discovery/usage rows).
    ccCommits: 0, ccLinesAdded: 0, ccLinesRemoved: 0, ccPullRequests: 0,
    // Claude interaction breakdown (the sub-metrics behind `interactions`).
    claudeWebSearches: 0, claudeConversations: 0, claudeMessages: 0,
    claudeCodeSessions: 0, claudeCoworkSessions: 0, claudeCoworkMessages: 0,
    // GitHub Copilot per-tool metrics from bronze__github_copilot
    // (also exposed by /discovery/usage rows). Copilot has no token
    // counts, so its spend is tracked separately as copilotCost.
    copilotInteractions: 0, copilotPremiumRequests: 0, copilotAiCredits: 0,
    // Included monthly AI-credit allowance ("free tier"); a monthly constant,
    // so it is aggregated with MAX (not summed) across days/members.
    copilotAiCreditQuota: 0,
    copilotCost: 0, copilotLinesAdded: 0,
    // GitHub Copilot interaction / output breakdown.
    copilotCodeGenerations: 0, copilotCodeAcceptances: 0, copilotLinesDeleted: 0,
    // Cursor / OpenAI API counts (no $). cursorLinesAdded/Deleted are the
    // AI lines the engineer accepted. Kept apart from the Claude
    // `interactions` / `totalTokens`, which mean Claude downstream.
    cursorRequests: 0, cursorTokens: 0,
    cursorLinesAdded: 0, cursorLinesDeleted: 0, cursorTabsAccepted: 0,
    codexRequests: 0, codexTokens: 0,
  };
}
// The Cursor / OpenAI API count fields of a daily row (all summed).
const CURSOR_CODEX_ROW_FIELDS = [
  'cursorRequests', 'cursorTokens',
  'cursorLinesAdded', 'cursorLinesDeleted', 'cursorTabsAccepted',
  'codexRequests', 'codexTokens',
];
// Every daily-row numeric field that is SUMMED across a series (as opposed to
// `copilotAiCreditQuota`, a monthly constant that is MAX'd instead — see
// addRowInto). Factored into a list so sumSeries/mergeSeriesList do a single
// data-driven loop instead of one `total.x += row.x || 0` statement per
// field, which is what was tripping the cyclomatic-complexity rule (each
// `||` is its own decision point) despite the logic being purely additive.
const SUMMABLE_ROW_FIELDS = [
  'interactions', 'inputTokens', 'cacheTokens', 'outputTokens', 'totalTokens', 'cost',
  'ccCommits', 'ccLinesAdded', 'ccLinesRemoved', 'ccPullRequests',
  'claudeWebSearches', 'claudeConversations', 'claudeMessages',
  'claudeCodeSessions', 'claudeCoworkSessions', 'claudeCoworkMessages',
  'copilotInteractions', 'copilotPremiumRequests', 'copilotAiCredits',
  'copilotCost', 'copilotLinesAdded',
  'copilotCodeGenerations', 'copilotCodeAcceptances', 'copilotLinesDeleted',
  ...CURSOR_CODEX_ROW_FIELDS,
];

// Accumulate one row's summable fields (+=) and its credit quota (MAX) into
// `total`, in place.
function addRowInto(total, row) {
  for (const f of SUMMABLE_ROW_FIELDS) {total[f] += row[f] || 0;}
  // Monthly allowance is a constant, not a daily figure — take the MAX.
  total.copilotAiCreditQuota = Math.max(total.copilotAiCreditQuota, row.copilotAiCreditQuota || 0);
}

// Merge one row's per-model maps into `total`'s, in place.
function mergeModelMapsInto(total, row) {
  for (const [mk, v] of Object.entries(row.perModel || {})) {total.perModel[mk] = (total.perModel[mk] || 0) + v;}
  for (const [mk, v] of Object.entries(row.perModelCost || {})) {total.perModelCost[mk] = (total.perModelCost[mk] || 0) + v;}
}

// Round the money fields of an accumulated row to `decimals` places, in place.
function roundMoneyFields(total, decimals) {
  total.cost = Number(total.cost.toFixed(decimals));
  total.copilotCost = Number(total.copilotCost.toFixed(decimals));
  for (const k of Object.keys(total.perModelCost)) {total.perModelCost[k] = Number(total.perModelCost[k].toFixed(decimals));}
  return total;
}

function sumSeries(series) {
  const total = emptyDailyRow(null);
  delete total.date;
  for (const row of series) {
    addRowInto(total, row);
    mergeModelMapsInto(total, row);
  }
  return roundMoneyFields(total, 2);
}

// ── Module cache populated by ensureFinanceDataLoaded ─────────────────
const _state = {
  ready: false,
  loading: null,                 // in-flight Promise
  daysLoaded: 0,                 // largest window successfully fetched
  warehouseAvailable: false,     // backend reported a registered destination
  realRoster: false,             // backend returned non-empty roster
  realUsage: false,              // backend returned >0 usage rows
  realProductivity: false,       // backend returned >0 productivity rows
  realEfficiency: false,         // backend returned non-zero efficiency totals
  // Per-slice readiness — each independent endpoint flips its flag the
  // moment it settles, so panels can render progressively instead of
  // waiting for the slowest call in the bundle.
  slices: {
    roster: false,
    usage: false,
    productivity: false,
    efficiency: false,
  },
  _tick: 0,                      // bumped on every notify() — drives re-render
  teams: [],
  members: [],
  projects: [],
  stories: [],
  efficiency: null,              // { totals, efficiency, cost_by_model, ... }
  // Real per-member daily rows keyed by member.email (lowercase) → day → row.
  usageByEmail: {},
  // Real per-team daily rows keyed by team.id → day → row.
  productivityByTeam: {},
  lastFetchedAt: 0,
  // True when the backend capped the usage feed, so any total derived from it
  // is UNDERSTATED. Surfaced to the user rather than logged — an unread flag
  // is no better than the server-side log it replaced.
  usageTruncated: false,
  // Which scope the per-member usage slice was loaded for; see
  // ensureFinanceDataLoaded. `null` = never declared.
  usageScope: null,
};

const STALE_MINUTES = 5;
const STALE_MS = STALE_MINUTES * 60 * 1000;

// ── Lightweight pub/sub so React components re-render as each data
// slice arrives (incremental loading), not just when everything is done.
const _subscribers = new Set();
function _notify() {
  _state._tick += 1;
  for (const fn of [..._subscribers]) {
    try { fn(_state._tick); } catch { /* ignore subscriber errors */ }
  }
}

/** Subscribe to finance-cache changes. Returns an unsubscribe fn. */
export function subscribeFinanceData(fn) {
  _subscribers.add(fn);
  return () => _subscribers.delete(fn);
}

/**
 * Wipe all cached finance/roster/usage data. MUST be called on logout and on
 * user switch — this module-level cache is process-global, so without a reset
 * the next user signing in on the same tab would briefly see the previous
 * user's roster/teams/members/usage (single-tenant login does not full-reload
 * the page). Bumps the tick so any mounted subscriber re-renders empty.
 */
export function resetFinanceData() {
  _state.ready = false;
  _state.loading = null;
  _state.daysLoaded = 0;
  _state.warehouseAvailable = false;
  _state.realRoster = false;
  _state.realUsage = false;
  _state.usageTruncated = false;
  _state.realProductivity = false;
  _state.realEfficiency = false;
  _state.slices = { roster: false, usage: false, productivity: false, efficiency: false };
  _state.teams = [];
  _state.members = [];
  _state.projects = [];
  _state.stories = [];
  _state.efficiency = null;
  _state.usageByEmail = {};
  _state.productivityByTeam = {};
  _state.lastFetchedAt = 0;
  _state.usageScope = null;
  // Scope-keyed usage summaries are per-user data too — a stale entry here
  // would show the previous user's org totals after a switch.
  _resetSummaryCache();
  _notify();
}


function _resetTeamMemberLinks() {
  const byId = Object.fromEntries(_state.teams.map(t => [t.id, t]));
  for (const t of Object.values(byId)) {t.members = Array.isArray(t.members) ? [...t.members] : [];}
  return byId;
}

/**
 * Stable identity for a usage scope, so a re-render with an equal-but-new object
 * does not look like a scope change and refetch 174 MB.
 *
 * Scopes are: `'org'` (needs no per-member rows at all), `'pending'` (a scope is
 * coming but its inputs have not resolved yet — do not fetch), `{ userEmail }`,
 * `{ teamId }`, `{ unitTier, unitId }`, or null for "deliberately unscoped".
 * `undefined` means "do not change what is loaded" and is what section
 * components pass.
 */
function usageScopeKey(scope) {
  if (!scope) {return '';}
  if (scope === 'org') {return 'org';}
  if (scope === 'pending') {return 'pending';}
  return ['u', scope.userEmail || '', 't', scope.teamId || '',
    'ut', scope.unitTier ?? '', 'ui', scope.unitId || ''].join(':');
}

/** Query string for /discovery/usage under this scope ('' when unscoped). */
function usageScopeQuery(scope) {
  if (!scope || scope === 'org' || scope === 'pending') {return '';}
  const p = new URLSearchParams();
  if (scope.userEmail) {p.set(USER_EMAIL, scope.userEmail);}
  else if (scope.unitTier != null && scope.unitId) {
    p.set('unit_tier', String(scope.unitTier));
    p.set('unit_id', scope.unitId);
  } else if (scope.teamId) {p.set('team_id', scope.teamId);}
  else { /* no recognised scope dimension — empty query */ }
  const q = p.toString();
  return q ? `&${q}` : '';
}

/**
 * Record which scope the per-member usage slice should hold. Returns true when
 * it changed (so the caller knows to refetch).
 *
 * MUST be callable during RENDER, not only from an effect, and that is the whole
 * subtlety. React runs a child's useEffect BEFORE its parent's, so OrgView
 * declaring 'org' in an effect lost the race to AIUsagePanel's own
 * useFinanceData(days) effect: the child kicked off the unscoped load first and
 * the 174 MB feed was fetched anyway. Render order is the other way round —
 * parent renders before child — so the declaration happens there.
 *
 * Idempotent: re-declaring the same scope is a no-op, which is what makes it
 * safe to call on every render.
 */
export function declareUsageScope(scope) {
  const key = usageScopeKey(scope);
  if (key === usageScopeKey(_state.usageScope)) {return false;}
  // A scope change invalidates the usage slice specifically — roster,
  // productivity and efficiency are scope-independent and stay put.
  _state.usageScope = scope;
  _state.slices.usage = false;
  _state.usageByEmail = {};
  return true;
}

// ── Usage slice — the /discovery/usage cache-loading task ─────────────────
// Split out of ensureFinanceDataLoaded's task list (was a single 400-line
// closure with a complexity in the 40s+) into one function per scope branch
// plus pure row-shaping helpers, each simple enough to sit under Sonar's
// per-function complexity limits on its own.

// Fields on a /discovery/usage row that default to 0 when absent. Looping
// over this list (rather than one `field: row.field || 0,` per line) keeps
// shapeUsageRow's own complexity down — each `||` is a decision point, and
// this row has 22 of them.
const USAGE_ROW_DEFAULT_ZERO_FIELDS = [
  'interactions', 'inputTokens', 'cacheTokens', 'outputTokens',
  'ccCommits', 'ccLinesAdded', 'ccLinesRemoved', 'ccPullRequests',
  'claudeWebSearches', 'claudeConversations', 'claudeMessages',
  'claudeCodeSessions', 'claudeCoworkSessions', 'claudeCoworkMessages',
  'copilotInteractions', 'copilotPremiumRequests', 'copilotAiCredits',
  'copilotAiCreditQuota', 'copilotLinesAdded',
  'copilotCodeGenerations', 'copilotCodeAcceptances', 'copilotLinesDeleted',
  ...CURSOR_CODEX_ROW_FIELDS,
];

// Shape one raw /discovery/usage row into the cache's per-day row format.
function shapeUsageRow(row) {
  const shaped = {
    date: row.date,
    totalTokens: (row.inputTokens || 0) + (row.outputTokens || 0),
    cost: Number((row.cost || 0).toFixed(4)),
    perModel: row.perModel || {},
    perModelCost: row.perModelCost || {},
    copilotCost: Number((row.copilotCost || 0).toFixed(4)),
  };
  for (const f of USAGE_ROW_DEFAULT_ZERO_FIELDS) {shaped[f] = row[f] || 0;}
  return shaped;
}

// Group /discovery/usage rows into { email -> { day -> shapedRow } }.
function groupUsageRowsByEmail(rows) {
  const byEmail = {};
  for (const row of rows) {
    const e = (row.memberEmail || '').toLowerCase();
    if (!e) {continue;}
    byEmail[e] = byEmail[e] || {};
    byEmail[e][row.date] = shapeUsageRow(row);
  }
  return byEmail;
}

// 'pending' scope: a page WILL declare a scope but has not resolved it yet
// (its team id or unit id is still loading). Fetching now would pull the
// whole cross product before the scope arrives, which is exactly what
// happened: TeamView opened with an empty team id, the feed went out
// unscoped, and 166 MB was already in flight by the time the id resolved.
// "I do not know the scope yet" must not mean "fetch everything".
function markUsagePending() {
  _state.usageByEmail = {};
  _state.realUsage = false;
  // slices.usage stays FALSE: panels keep their skeletons rather than
  // rendering an empty state, and the real scope's fetch will flip it.
  _notify();
}

// True when a usage-summary total shows any real activity. Named out (rather
// than one big `A>0 || B>0 || C>0 || D>0` with each operand also `|| 0`
// defaulted) to keep any single expression's conditional-operator count
// within Sonar's limit of 3.
function summaryHasActivity(total) {
  const t = total || {};
  const hasInteractions = (Number(t.interactions) || 0) > 0;
  const hasTokens = (Number(t.totalTokens) || 0) > 0;
  const hasCost = (Number(t.cost) || 0) > 0;
  const hasCopilotInteractions = (Number(t.copilotInteractions) || 0) > 0;
  const hasCursorOrCodex = CURSOR_CODEX_ROW_FIELDS.some(f => (Number(t[f]) || 0) > 0);
  return [hasInteractions, hasTokens, hasCost, hasCopilotInteractions, hasCursorOrCodex].some(Boolean);
}

// Org scope: no consumer on that page reads a per-member row, so the 174 MB
// feed buys nothing. One day-grained call gives `realUsage` and warms the
// cache getOrgUsage/getTeamUsage read.
async function loadOrgUsage(days) {
  try {
    const sum = await fetchUsageSummary({ days });
    _state.realUsage = summaryHasActivity(sum && sum.total);
    if (sum && Array.isArray(sum.series)) {
      _summaryCache.set(_summaryKey({ days }), { series: sum.series, total: sum.total });
    }
  } catch (err) {
    _state.realUsage = false;
  }
  // Deliberately left empty: on org scope nothing reads it, and leaving a
  // previous scope's rows here would let a member panel render another
  // scope's numbers if one were ever mounted.
  _state.usageByEmail = {};
  _state.usageTruncated = false;
  _state.slices.usage = true;
  _notify();
}

// Scoped when a page declared one: /discovery/usage pushes these into SQL,
// so scoping to one person at days=365 is 5.9s / 0.15 MB against 35s / 174 MB
// unscoped.
async function loadScopedUsage(days) {
  try {
    const scopeQ = usageScopeQuery(_state.usageScope);
    const u = (await apiGet(`/discovery/usage?days=${days}${scopeQ}`)) || {};
    const rows = u.rows || [];
    _state.usageTruncated = u.truncated === true;
    _state.realUsage = rows.length > 0;
    _state.usageByEmail = groupUsageRowsByEmail(rows);
  } catch (err) {
    _state.realUsage = false;
    _state.usageByEmail = {};
  } finally {
    _state.slices.usage = true;
    _notify();
  }
}

async function loadUsageSlice(days) {
  if (_state.usageScope === 'pending') {return markUsagePending();}
  if (_state.usageScope === 'org') {return loadOrgUsage(days);}
  return loadScopedUsage(days);
}

// ── Productivity slice ─────────────────────────────────────────────────
const PRODUCTIVITY_ROW_DEFAULT_ZERO_FIELDS = [
  'velocity', 'linesOfCode', 'commits', 'prsOpened', 'prsMerged', 'incidents',
];

function shapeProductivityRow(row) {
  const shaped = { date: row.date };
  for (const f of PRODUCTIVITY_ROW_DEFAULT_ZERO_FIELDS) {shaped[f] = row[f] || 0;}
  return shaped;
}

function groupProductivityRowsByTeam(rows) {
  const byTeam = {};
  for (const row of rows) {
    const tid = row.teamId;
    if (!tid) {continue;}
    byTeam[tid] = byTeam[tid] || {};
    byTeam[tid][row.date] = shapeProductivityRow(row);
  }
  return byTeam;
}

async function loadProductivitySlice(days) {
  try {
    const p = (await apiGet(`/discovery/productivity?days=${days}`)) || {};
    const rows = p.rows || [];
    _state.realProductivity = rows.length > 0;
    _state.productivityByTeam = groupProductivityRowsByTeam(rows);
  } catch (err) {
    _state.realProductivity = false;
    _state.productivityByTeam = {};
  } finally {
    _state.slices.productivity = true;
    _notify();
  }
}

// ── Efficiency slice ───────────────────────────────────────────────────
// [apiFieldName, cacheFieldName] pairs for the array-valued efficiency
// breakdowns — all shaped the same way (Array.isArray guard, else []).
const EFFICIENCY_ARRAY_FIELD_MAP = [
  ['cost_by_model', 'costByModel'],
  ['cost_by_token_type', 'costByTokenType'],
  ['cost_over_time', 'costOverTime'],
  ['activity_over_time', 'activityOverTime'],
  ['cost_by_tool', 'costByTool'],
  ['copilot_by_model', 'copilotByModel'],
];

function shapeEfficiencyPayload(e, days) {
  const shaped = {
    warehouseAvailable: Boolean(e.warehouse_available),
    days: e.days || days,
    totals: e.totals || {},
    ratios: e.efficiency || {},
  };
  for (const [srcKey, outKey] of EFFICIENCY_ARRAY_FIELD_MAP) {
    shaped[outKey] = Array.isArray(e[srcKey]) ? e[srcKey] : [];
  }
  return shaped;
}

// True when the efficiency totals show any real commits/cost/output. Named
// booleans keep the final expression's conditional-operator count at 3.
function efficiencyHasActivity(totals) {
  if (!totals) {return false;}
  const hasCost = Number(totals.cost_usd) > 0;
  const hasCommits = Number(totals.commits) > 0;
  const hasLinesAdded = Number(totals.lines_added) > 0;
  const hasStoryPoints = Number(totals.story_points_accepted) > 0;
  return hasCost || hasCommits || hasLinesAdded || hasStoryPoints;
}

async function loadEfficiencySlice(days) {
  try {
    const e = (await apiGet(`/discovery/efficiency?days=${days}`)) || {};
    _state.efficiency = shapeEfficiencyPayload(e, days);
    _state.realEfficiency = efficiencyHasActivity(_state.efficiency.totals);
  } catch (err) {
    _state.efficiency = null;
    _state.realEfficiency = false;
  } finally {
    _state.slices.efficiency = true;
    _notify();
  }
}

// Whether an in-flight/just-finished load's usage slice was invalidated by a
// scope declared WHILE it ran (see the comment in ensureFinanceDataLoaded's
// `if (_state.loading)` branch for the race this guards against).
function usageSliceInvalidated(state) {
  return state.slices.usage === false && state.usageScope && state.usageScope !== 'pending';
}

// Whether the cached data already covers this request (ready, enough days,
// not stale) — the fast path that lets ensureFinanceDataLoaded skip a reload.
function cacheSatisfiesRequest(state, days, fresh) {
  return state.ready && state.daysLoaded >= days && fresh;
}

// Resolve the effective `force` flag for a load: a declared scope change, or
// an invalidated usage slice on an otherwise-ready cache, both force a reload
// even when the caller didn't ask for one. See ensureFinanceDataLoaded.
function resolveForceReload(force, usageScope) {
  // `usageScope` decides whether the PER-MEMBER usage feed is fetched at all.
  //
  // /discovery/usage is O(members x days): 266,698 rows / 174 MB / ~44s at
  // days=365 on the seeded org. It was fetched unconditionally, org-wide, on
  // every page — including pages that render nothing per person. The Org view is
  // exactly that: its "Team usage" table reads per-TEAM totals (getTeamUsage,
  // now a server-side day aggregate) and its per-member list is literally `[]`.
  // So org scope skips the feed and reads /discovery/usage-summary instead —
  // one cheap call that also gives `realUsage`.
  //
  // `undefined` means "no page has declared a scope", which is treated as the
  // old behaviour (fetch everything). Only an explicit `'org'` skips: a page
  // that needs per-member rows can never lose them by omission, only by
  // declaring the one scope that has none.
  if (usageScope !== undefined && declareUsageScope(usageScope)) {return true;}
  // A scope declared during RENDER (declareUsageScope, which page-level views
  // call directly) invalidates the usage slice without going through this
  // function, so the freshness check below would return early and the resolved
  // scope would never be fetched. That is worse than fetching too much: the
  // panels sit on skeletons forever. An invalidated usage slice on an otherwise
  // ready cache is therefore a reason to reload.
  if (!force && _state.ready && _state.slices.usage === false) {return true;}
  return force;
}

/**
 * Fetch the latest roster / usage / productivity into the cache. Safe
 * to call from many components — the in-flight promise is shared.
 */
export async function ensureFinanceDataLoaded({ days = 30, force = false, usageScope = undefined } = {}) {
  force = resolveForceReload(force, usageScope);
  const fresh = Date.now() - _state.lastFetchedAt < STALE_MS;
  if (!force && cacheSatisfiesRequest(_state, days, fresh)) {
    return _state;
  }
  if (_state.loading) {
    await _state.loading;
    // The load we just waited on may have run against a stale scope: a
    // page-level declareUsageScope call can flip `usageScope` from 'pending'
    // to a real value WHILE that load is still in flight (its usage task
    // already committed to 'pending' and will leave `slices.usage` false).
    // Checking `daysLoaded` alone would call that load "done" and return,
    // permanently starving the real scope's fetch — so also check whether
    // the usage slice is invalidated under a since-resolved real scope.
    if (_state.daysLoaded >= days && !usageSliceInvalidated(_state)) {return _state;}
  }
  _state.loading = (async () => {
    // Each endpoint runs as its own task: it applies its slice of the
    // cache, flips its readiness flag, and notifies subscribers the
    // instant it settles. Panels keyed on the per-slice flag therefore
    // paint as soon as *their* data lands, rather than blocking on the
    // slowest call in the bundle.
    const tasks = [];

    // ── Roster ─────────────────────────────────────────────────────
    tasks.push((async () => {
      try {
        const r = (await apiGet('/discovery/roster')) || {};
        _state.warehouseAvailable = Boolean(r.warehouse_available);
        const hasReal = (r.teams && r.teams.length) || (r.members && r.members.length);
        _state.realRoster = Boolean(hasReal);
        _state.teams = (r.teams || []).map(t => ({
          id: t.id, name: t.name, owner: t.owner,
          members: Array.isArray(t.members) ? [...t.members] : [],
        }));
        _state.members = (r.members || []).map(m => ({
          id: m.id, name: m.name, email: m.email, role: m.role,
        }));
        _state.projects = (r.projects || []).map(p => ({
          id: p.id, name: p.name, teamId: p.teamId || null,
        }));
        _state.stories = (r.stories || []).map(s => ({
          id: s.id, title: s.title || s.id,
          projectId: s.projectId || null, teamId: s.teamId || null,
          owner: s.owner, planEstimate: s.planEstimate, iteration: s.iteration,
        }));
      } catch (err) {
        _state.warehouseAvailable = false;
        _state.realRoster = false;
        _state.teams = [];
        _state.members = [];
        _state.projects = [];
        _state.stories = [];
      } finally {
        _resetTeamMemberLinks();
        _state.slices.roster = true;
        _notify();
      }
    })());

    // ── Usage ──────────────────────────────────────────────────────
    tasks.push(loadUsageSlice(days));

    // ── Productivity ───────────────────────────────────────────────
    tasks.push(loadProductivitySlice(days));

    // ── Efficiency ─────────────────────────────────────────────────
    tasks.push(loadEfficiencySlice(days));

    await Promise.allSettled(tasks);
    _state.ready = true;
    _state.daysLoaded = Math.max(_state.daysLoaded, days);
    _state.lastFetchedAt = Date.now();
    _state.loading = null;
    _notify();
    return _state;
  })();
  return _state.loading;
}

/**
 * React hook — drives the page through the loading lifecycle. Triggers
 * a refetch whenever ``days`` grows past what's cached.
 */
export function useFinanceData(days = 30, pollSeconds = 0, usageScope = undefined) {
  // `usageScope` is declared by PAGE-level views only, and only to say "this
  // page renders nothing per person" ('org'). Section components keep calling
  // useFinanceData(days) and leave it undefined, which means "don't change what
  // is loaded" — so a panel that needs per-member rows can never drop them by
  // failing to pass an argument. See ensureFinanceDataLoaded.
  const [, forceRender] = useState(0);
  // Declared during RENDER, deliberately — see declareUsageScope. Doing it in
  // the effect below would run after the children's effects, by which time an
  // unscoped load is already in flight.
  if (usageScope !== undefined) {declareUsageScope(usageScope);}
  const renderedTick = _state._tick;
  useEffect(() => {
    // Re-render on every slice notification so panels paint progressively.
    const unsub = subscribeFinanceData(() => forceRender(v => v + 1));
    // The subscription starts here, after the render. A slice that landed in
    // between notified nobody, so catch up on it now instead of waiting for the
    // next one — which may never come once the load has finished.
    if (_state._tick !== renderedTick) {forceRender(v => v + 1);}
    ensureFinanceDataLoaded({ days, usageScope });
    // Optional polling: auto-refresh so data doesn't go stale. Exactly one
    // timer per mounted hook; cleared on unmount and when days/pollSeconds change.
    let timer = null;
    if (pollSeconds > 0) {
      timer = setInterval(() => {
        ensureFinanceDataLoaded({ days, force: true, usageScope });
      }, pollSeconds * 1000);
    }
    return () => {
      unsub();
      if (timer) {clearInterval(timer);}
    };
    // `renderedTick` is read once, for the gap before the subscription; keying
    // the effect on it would resubscribe on every notification.
  }, [days, pollSeconds, usageScope]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () => ensureFinanceDataLoaded({ days, force: true, usageScope });
  return {
    ready: _state.ready,
    warehouseAvailable: _state.warehouseAvailable,
    realRoster: _state.realRoster,
    realUsage: _state.realUsage,
    usageTruncated: _state.usageTruncated,
    realProductivity: _state.realProductivity,
    realEfficiency: _state.realEfficiency,
    // Per-slice readiness for progressive skeletons.
    slices: { ..._state.slices },
    rosterReady: _state.slices.roster,
    usageReady: _state.slices.usage,
    productivityReady: _state.slices.productivity,
    efficiencyReady: _state.slices.efficiency,
    lastFetchedAt: _state.lastFetchedAt,
    refresh,
    version: _state._tick,
  };
}

export function getEfficiency() {
  return _state.efficiency;
}

// ── Spend-by-tool (live fetch, not cached) ─────────────────────────────
// Used by the "Spend by AI tool" card on My Discovery and by the
// AI Billing per-tool breakdown.
export async function fetchSpendByTool({ days = 30, teamId, userEmail, unitTier, unitId } = {}) {
  const params = new URLSearchParams({ days: String(days) });
  if (teamId) {params.set('team_id', teamId);}
  if (userEmail) {params.set(USER_EMAIL, userEmail);}
  if (unitTier != null) {params.set('unit_tier', String(unitTier));}
  if (unitId) {params.set('unit_id', unitId);}
  try {
    return await apiGet(`/discovery/spend-by-tool?${params.toString()}`);
  } catch (err) {
    logger.warn('[discovery] spend-by-tool failed', err);
    return { warehouse_available: false, days, total_usd: 0, rows: [], catalogue: [] };
  }
}

// ── Usage by AI product (non-financial, live fetch) ───────────────────
// Per-product interaction volume (Claude Code, Claude.ai, Cowork, Research,
// … + GitHub Copilot) for the AI Usage tab's "Usage by AI tool" chart.
// Unit is interactions (Anthropic `requests` per product + Copilot
// user-initiated interactions); token volume rides along for tooltips.
export async function fetchUsageDetail({ days = 30, teamId, teamIds, userEmail, unitTier, unitId } = {}) {
  const params = new URLSearchParams({ days: String(days) });
  if (teamId) {params.set('team_id', teamId);}
  if (teamIds && teamIds.length) {params.set('team_ids', teamIds.join(','));}
  if (userEmail) {params.set(USER_EMAIL, userEmail);}
  if (unitTier != null) {params.set('unit_tier', String(unitTier));}
  if (unitId) {params.set('unit_id', unitId);}
  try {
    return await apiGet(`/discovery/usage-detail?${params.toString()}`);
  } catch (err) {
    logger.warn('[discovery] usage-detail failed', err);
    return {
      warehouse_available: false, days,
      claude: { metrics: {}, tokensByModel: [], tokensByProduct: [] },
      copilot: { metrics: {}, premiumByModel: [], creditsByModel: [] },
    };
  }
}

// ── Billing breakdown (license vs. metered, live fetch) ────────────────
// License-vs-metered split across Claude and GitHub Copilot. Both tools
// bill as "subscription license + metered overage"; this endpoint
// surfaces the four components (Claude license, Claude token, Copilot
// license, Copilot premium requests). Honours the same team / user
// filters as fetchSpendByTool.
export async function fetchBillingBreakdown({ days = 30, teamId, userEmail, unitTier, unitId } = {}) {
  const params = new URLSearchParams({ days: String(days) });
  if (teamId) {params.set('team_id', teamId);}
  if (userEmail) {params.set(USER_EMAIL, userEmail);}
  if (unitTier != null) {params.set('unit_tier', String(unitTier));}
  if (unitId) {params.set('unit_id', unitId);}
  try {
    return await apiGet(`/discovery/billing-breakdown?${params.toString()}`);
  } catch (err) {
    logger.warn('[discovery] billing-breakdown failed', err);
    return { warehouse_available: false, days, total_usd: 0, components: [] };
  }
}

// ── Billing over time (stacked-area + pie, live fetch) ─────────────────
// Per-day version of fetchBillingBreakdown: the same components
// (GH Copilot license, GH Copilot premium requests, Claude license,
// Claude out-of-license tokens, Microsoft 365 Copilot license, Cursor
// license, Cursor usage, OpenAI API usage) spread across the day range.
// useBudgetData.BILLING_POINT_KEYS lists the per-day keys. Metered halves follow their real per-day shape;
// license halves are flat estimates. Sums to the same totals as
// fetchBillingBreakdown so the area chart and pie reconcile with the card.
export async function fetchBillingOverTime({ days = 30, teamId, userEmail, unitTier, unitId } = {}) {
  const params = new URLSearchParams({ days: String(days) });
  if (teamId) {params.set('team_id', teamId);}
  if (userEmail) {params.set(USER_EMAIL, userEmail);}
  if (unitTier != null) {params.set('unit_tier', String(unitTier));}
  if (unitId) {params.set('unit_id', unitId);}
  try {
    return await apiGet(`/discovery/billing-over-time?${params.toString()}`);
  } catch (err) {
    logger.warn('[discovery] billing-over-time failed', err);
    return { warehouse_available: false, days, dates: [], series: [], components: [], total_usd: 0 };
  }
}

// ── Period-anchored cost KPIs (MTD / forecast / YTD) ──────────────────
// Four headline figures for the AI Billing tab, each with a
// period-over-period comparison %. Same seats + metered math as
// fetchBillingBreakdown, narrowed by the same team/user filter.
export async function fetchCostMetrics({ teamId, userEmail, unitTier, unitId } = {}) {
  const params = new URLSearchParams();
  if (teamId) {params.set('team_id', teamId);}
  if (userEmail) {params.set(USER_EMAIL, userEmail);}
  if (unitTier != null) {params.set('unit_tier', String(unitTier));}
  if (unitId) {params.set('unit_id', unitId);}
  const qs = params.toString();
  try {
    return await apiGet(withQuery('/discovery/cost-metrics', qs));
  } catch (err) {
    logger.warn('[discovery] cost-metrics failed', err);
    return {
      warehouse_available: false,
      month_to_date: {}, forecast_month: {},
      year_to_date: {}, forecast_year: {},
    };
  }
}

// ── AWS Bedrock product cost (the "Product Cost" billing category) ─────
// Org-level LLM spend the product itself runs on (AWS Bedrock), as
// opposed to the Engineer Harness tools (Claude/Copilot). Headline
// total_usd is authoritative Cost Explorer spend; by_provider / by_model
// are token-rate calculated (see /discovery/bedrock-cost). No team/user
// filter — product cost is org-wide.
export async function fetchBedrockCost({ days = 30 } = {}) {
  const params = new URLSearchParams({ days: String(days) });
  try {
    return await apiGet(`/discovery/bedrock-cost?${params.toString()}`);
  } catch (err) {
    logger.warn('[discovery] bedrock-cost failed', err);
    return {
      warehouse_available: false, available: false, days,
      total_usd: 0, ce_total_usd: 0, calc_total_usd: 0, basis: 'none',
      by_provider: [], by_model: [], cost_over_time: [],
    };
  }
}

// ── Azure AI Foundry product cost (the Azure half of "Product Cost") ──
// Org-level LLM spend of products and agents on Azure AI Foundry / Azure
// OpenAI. total_usd is the Cost Management bill; by_model is Azure's
// per-model estimate rescaled to it (or the billed meters). No team/user
// filter — product cost is org-wide.
export async function fetchAzureFoundryCost({ days = 30 } = {}) {
  const params = new URLSearchParams({ days: String(days) });
  try {
    return await apiGet(`/discovery/azure-foundry-cost?${params.toString()}`);
  } catch (err) {
    logger.warn('[discovery] azure-foundry-cost failed', err);
    return {
      warehouse_available: false, available: false, days,
      total_usd: 0, cost_total_usd: 0, estimated_total_usd: 0, currency: 'USD', basis: 'none',
      by_provider: [], by_model: [], cost_over_time: [],
      usage: { requests: 0, input_tokens: 0, output_tokens: 0, projects: 0 }, tokens_by_model: [],
    };
  }
}

// ── Per-engineer billing (live fetch) ──────────────────────────────────
// Returns one fully-loaded billing total per team member, computed with
// the SAME math as fetchBillingBreakdown (license + token + Copilot). The
// Team View "Per-engineer spend" table uses this so each engineer's cost
// equals their User View "Cost (window)" KPI and the two pages reconcile.
//
// **Pass `limit`/`offset` — the per-day arrays exist only for a page.** Each
// row would otherwise carry two arrays (`daily`, `dailyMetered`) of one entry
// per day, and a whole roster of those measured 26 MB / 14s at days=365, to
// fill a table that renders ten rows. An unpaged call now returns the same
// roster and the same totals WITHOUT them (0.17 MB) and sets `series_omitted`,
// so asking for a sparkline without paging gets you a missing field rather
// than a 26 MB download. Paged, the same first page is ~270 KB.
//
// The response always carries `total_members` (the full in-scope count) plus
// `limit` / `offset` / `has_more` when paging, so the caller can render
// "1-10 of 1,264" instead of silently showing a subset. Ordering is exact
// descending spend with a stable tiebreak, so a member appears on exactly one
// page.
//
// If you need one number per person for the WHOLE roster (summing, or joining
// spend onto another list), use fetchMemberSpendTotals below — do NOT page this
// and aggregate, or every off-page member reads as $0.
export async function fetchBillingByMember({
  days = 30, teamId, unitTier, unitId, start, end, limit, offset,
} = {}) {
  const params = new URLSearchParams({ days: String(days) });
  if (teamId) {params.set('team_id', teamId);}
  if (unitTier != null) {params.set('unit_tier', String(unitTier));}
  if (unitId) {params.set('unit_id', unitId);}
  // Explicit ISO window (prior-period per-member spend) overrides `days`.
  if (start && end) { params.set('start', start); params.set('end', end); }
  if (limit != null) {params.set('limit', String(limit));}
  if (offset != null && offset > 0) {params.set('offset', String(offset));}
  try {
    return await apiGet(`/discovery/billing-by-member?${params.toString()}`);
  } catch (err) {
    logger.warn('[discovery] billing-by-member failed', err);
    return { warehouse_available: false, days, members: [] };
  }
}

// ── Day-grained usage summary (server-side aggregate) ─────────────────
//
// /discovery/usage-summary returns ONE row per day for a scope. It replaces the
// browser summing /discovery/usage's per-(member, day) rows: getUnitUsage /
// getTeamUsage / getOrgUsage below used to walk every member in scope and merge
// their series, which on the seeded org at days=365 meant 266,698 rows and
// 174 MB moved to draw one chart. The server does the same arithmetic — asserted
// cell-by-cell by TC-US-01 — over 365 rows.
export async function fetchUsageSummary({ days = 30, teamId, unitTier, unitId, userEmail } = {}) {
  const params = new URLSearchParams({ days: String(days) });
  if (userEmail) {params.set(USER_EMAIL, userEmail);}
  else if (unitTier != null && unitId) { params.set('unit_tier', String(unitTier)); params.set('unit_id', unitId); }
  else if (teamId) {params.set('team_id', teamId);}
  else { /* no scope filter — org-wide */ }
  try {
    return await apiGet(`/discovery/usage-summary?${params.toString()}`);
  } catch (err) {
    logger.warn('[discovery] usage-summary failed', err);
    return null;
  }
}

// Scope-keyed cache so the SYNCHRONOUS accessors below keep their signature.
//
// getUnitUsage/getTeamUsage/getOrgUsage are called straight out of useMemo in
// five components. Making them async would push loading state into every one of
// those call sites; instead they ask this cache, and a miss kicks off the fetch
// and _notify()s on arrival — the same pub/sub the slice loader already uses to
// repaint. Until it lands the accessor falls back to the old client-side sum, so
// nothing renders blank and a failed request degrades rather than empties.
const _summaryCache = new Map();   // key -> { series, total } | 'pending'

// Bounded concurrency for the fan-out above.
//
// The org AI-Usage tab asks for one summary per team, and `_usageSummary` used
// to fire each one the instant its accessor was called: 52 requests dispatched
// in the same tick on the seeded org. A browser opens ~6 connections per host,
// so those 52 filled the pool and everything queued behind them.
//
// That is not merely wasteful — it STARVED A SIBLING TAB. Measured on
// afm.fsofter.com, walking the Discovery tabs in order: Overview drew in 554ms,
// AI Adoption 4.9s, AI Usage 74ms, and then Productivity was BLANK for 25s
// (2 spinners, 6 skeletons, no error) with its three requests still unstarted
// while 52 usage-summary calls sat in flight at 27.7s. That is the whole of
// MT-DISCOVERY's "org tab: /productivity/i" failure.
//
// A cap of 4 leaves headroom in the per-host budget for whatever else the page
// needs, which is the property that matters — a panel may not consume the
// connection pool just because it happens to render first. It does not make the
// fan-out cheaper; batching it into one request is TD-7 and still worth doing.
const _SUMMARY_MAX_INFLIGHT = 4;
let _summaryInflight = 0;
const _summaryQueue = [];

function _pumpSummaryQueue() {
  while (_summaryInflight < _SUMMARY_MAX_INFLIGHT && _summaryQueue.length) {
    const job = _summaryQueue.shift();
    _summaryInflight += 1;
    // `job` already swallows its own errors; `finally` is what keeps the pump
    // alive if one ever throws synchronously inside the promise chain.
    Promise.resolve()
      .then(job)
      .finally(() => { _summaryInflight -= 1; _pumpSummaryQueue(); });
  }
}

// One repaint for summaries that land close together.
//
// They arrive one per team — 123 on the seeded org — and every _notify()
// re-renders every subscribed panel, the org page's whole tab set among them.
// Notified one at a time they kept the main thread busy for most of the minute
// after AI Usage was opened. Slice notifications stay immediate; only these
// are coalesced, so the team table still fills in as the summaries come.
const _SUMMARY_NOTIFY_MS = 300;
let _summaryNotifyTimer = null;

function _notifySummaryLanded() {
  if (_summaryNotifyTimer) {return;}
  _summaryNotifyTimer = setTimeout(() => {
    _summaryNotifyTimer = null;
    _notify();
  }, _SUMMARY_NOTIFY_MS);
}

function _summaryKey({ days, teamId, unitTier, unitId, userEmail }) {
  return [days, userEmail || '', unitTier ?? '', unitId || '', teamId || ''].join('|');
}

/** Cached day-grained summary for a scope, or null while it loads. */
function _usageSummary(args) {
  const key = _summaryKey(args);
  const hit = _summaryCache.get(key);
  if (hit && hit !== 'pending') {return hit;}
  if (hit === 'pending') {return null;}
  _summaryCache.set(key, 'pending');
  _summaryQueue.push(() => fetchUsageSummary(args).then((r) => {
    if (!r || r.warehouse_available === false || !Array.isArray(r.series)) {
      // Remember the miss as a resolved empty rather than deleting the key —
      // deleting would let every re-render fire the request again.
      _summaryCache.set(key, { series: null, total: null });
    } else {
      _summaryCache.set(key, { series: r.series, total: r.total });
    }
    _notifySummaryLanded();
  }));
  _pumpSummaryQueue();
  return null;
}

/** Wipe the summary cache — called from resetFinanceData on logout/user switch. */
function _resetSummaryCache() {
  _summaryCache.clear();
  // Drop queued work too. Without this, a logout leaves the previous user's
  // pending team fetches in the queue to run against the new session.
  _summaryQueue.length = 0;
  // resetFinanceData notifies straight after this, so a pending coalesced
  // repaint has nothing left to add.
  clearTimeout(_summaryNotifyTimer);
  _summaryNotifyTimer = null;
}

// ── Per-member spend totals, whole roster (live fetch) ─────────────────
// `email -> total_usd` for EVERY member in scope, with no per-day arrays.
//
// The lookup counterpart to fetchBillingByMember: same billing math, same
// numbers, but affordable across the full roster (1,264 members measured ~1.4s
// / 216 KB, against ~17s / 33 MB for the same roster with the series).
//
// Use this — not a paged fetchBillingByMember — whenever the caller SUMS or
// JOINS across all members: a unit's own spend, a "cost by job title" rollup,
// filling a spend column on a list of people. Paging those would understate
// the total and show $0 for everyone off the page. Rows carry
// `{ id, email, name, role, total_usd, avg_cost_per_day }`.
export async function fetchMemberSpendTotals({
  days = 30, teamId, unitTier, unitId, start, end,
} = {}) {
  const params = new URLSearchParams({ days: String(days) });
  if (teamId) {params.set('team_id', teamId);}
  if (unitTier != null) {params.set('unit_tier', String(unitTier));}
  if (unitId) {params.set('unit_id', unitId);}
  if (start && end) { params.set('start', start); params.set('end', end); }
  try {
    return await apiGet(`/discovery/member-spend-totals?${params.toString()}`);
  } catch (err) {
    logger.warn('[discovery] member-spend-totals failed', err);
    return { warehouse_available: false, days, members: [] };
  }
}

// ── Cost-saving opportunities (server-side aggregation) ────────────────
// Replaces the former client rollup: the backend aggregates the gold
// member_day fact + bronze per-model cost and applies the configured saving
// rules, returning per-rule flagged rows + savings totals for the scope.
export async function fetchCostSavings({ days = 30, teamId, userEmail, unitTier, unitId } = {}) {
  const params = new URLSearchParams({ days: String(days) });
  if (teamId) {params.set('team_id', teamId);}
  if (userEmail) {params.set(USER_EMAIL, userEmail);}
  if (unitTier != null) {params.set('unit_tier', String(unitTier));}
  if (unitId) {params.set('unit_id', unitId);}
  try {
    return await apiGet(`/discovery/cost-savings?${params.toString()}`);
  } catch (err) {
    logger.warn('[discovery] cost-savings failed', err);
    return {
      warehouse_available: false, member_count: 0,
      premium: { rows: [], savings: 0 }, ms: { rows: [], savings: 0 },
      underuse: { rows: [], savings: 0 }, mix: { rows: [], savings: 0 },
      total_savings: 0, total_cost: 0,
    };
  }
}

// ── Per-(member, day) ACTUAL cost + consumption (Task×Cost timeline) ───
// True per-day grain (not a spread average) read from gold__ai_spend.member_day
// — the rollup of claude_code__user_cost_report + github premium/AI-credit
// daily (cost) and claude_code__user_activity + copilot enterprise_user_metrics
// (consumption). Fixed seat licenses are excluded. Member scope only.
export async function fetchMemberDailyTimeline({ days = 30, teamId, userEmail, unitTier, unitId } = {}) {
  const params = new URLSearchParams({ days: String(days) });
  if (teamId) {params.set('team_id', teamId);}
  if (userEmail) {params.set(USER_EMAIL, userEmail);}
  if (unitTier != null) {params.set('unit_tier', String(unitTier));}
  if (unitId) {params.set('unit_id', unitId);}
  try {
    return await apiGet(`/discovery/member-daily-timeline?${params.toString()}`);
  } catch (err) {
    logger.warn('[discovery] member-daily-timeline failed', err);
    return { warehouse_available: false, days, members: [] };
  }
}

// ── Per-direct-child-unit billing (tier dashboards' "by child tier" table) ──
// The Org-Structure analog of fetchBillingByTeam: one fully-loaded billing
// total per direct child unit of (tierLevel, unitId). Same `teams[]` row shape
// as fetchBillingByTeam so the AI Billing insight table renders it unchanged.
// Pass `start`/`end` (ISO dates) to bill an explicit window instead of the
// trailing `days` — the Budget tab's period-to-date and prior-period columns.
// Prefer this over one `unit-summary` per child: the date-only warehouse scans
// are shared across every child inside the one request, where N separate calls
// each re-ran them.
export async function fetchBillingByChildUnit({ days = 30, tierLevel, unitId, start, end } = {}) {
  const params = new URLSearchParams({ days: String(days) });
  if (tierLevel != null) {params.set('tier_level', String(tierLevel));}
  if (unitId) {params.set('unit_id', unitId);}
  if (start && end) { params.set('start', start); params.set('end', end); }
  try {
    return await apiGet(`/discovery/billing-by-child-unit?${params.toString()}`);
  } catch (err) {
    logger.warn('[discovery] billing-by-child-unit failed', err);
    return { warehouse_available: false, days, teams: [] };
  }
}

// ── Per-team billing (live fetch) ──────────────────────────────────────
// The team analog of fetchBillingByMember: one fully-loaded billing total
// per team, computed with the SAME math, so each team's cost equals the
// sum of its members' billing. The Org View "Team spend" table uses this.
export async function fetchBillingByTeam({ days = 30 } = {}) {
  const params = new URLSearchParams({ days: String(days) });
  try {
    return await apiGet(`/discovery/billing-by-team?${params.toString()}`);
  } catch (err) {
    logger.warn('[discovery] billing-by-team failed', err);
    return { warehouse_available: false, days, teams: [] };
  }
}

// ── Model-family thresholds (Cost Saving tab inputs) ──────────────────
// Admin-configured ideal/warning share targets per Claude family
// (haiku/sonnet/opus). Defaults to 33% ideal + 66% warning per family.
// Backend seeds on first read so callers always get three rows.
export const MODEL_FAMILIES = ['haiku', 'sonnet', 'opus'];
export const DEFAULT_MODEL_THRESHOLDS = MODEL_FAMILIES.map(family => ({
  family, idealPct: 33, warningPct: 66,
}));

export async function fetchModelThresholds() {
  try {
    const r = await apiGet('/discovery/model-thresholds');
    return r?.thresholds || DEFAULT_MODEL_THRESHOLDS;
  } catch (err) {
    logger.warn('[discovery] model-thresholds GET failed', err);
    return DEFAULT_MODEL_THRESHOLDS;
  }
}

export async function saveModelThresholds(thresholds) {
  const r = await apiSend('PUT', '/discovery/model-thresholds', { thresholds });
  return r?.thresholds || thresholds;
}

// ── Saving-rules config (Cost Saving tab whole-seat reclaim rules) ─────
// Admin-configured flags + underuse threshold backing the "Config saving
// rules" modal. The underuse threshold is the active-days % below which a
// held seat counts as "barely used". Defaults: all rules on, 20%.
export const DEFAULT_SAVING_RULES = {
  underuseThresholdPct: 20,
  premiumOverlapEnabled: true,
  msCopilotOverlapEnabled: true,
  underuseEnabled: true,
  modelMixEnabled: true,
};

export async function fetchSavingRules() {
  try {
    const r = await apiGet('/discovery/saving-rules');
    return { ...DEFAULT_SAVING_RULES, ...(r?.rules || {}) };
  } catch (err) {
    logger.warn('[discovery] saving-rules GET failed', err);
    return { ...DEFAULT_SAVING_RULES };
  }
}

export async function saveSavingRules(rules) {
  const r = await apiSend('PUT', '/discovery/saving-rules', { rules });
  return { ...DEFAULT_SAVING_RULES, ...(r?.rules || rules) };
}

// ── AI Billing tab aggregate (live fetch) ──────────────────────────────
// One call backing the AI Billing tab on every dashboard scope
// (individual / team / organization). Returns the reconciled billing
// totals (same single-source math as fetchBillingBreakdown) plus the
// tab-specific charts: per-tool & per-model pies, Claude usage cost over
// time + by product, and GitHub premium-request / AI-credit volume & cost
// over time split charged vs free. Honours the team / user scope filters.
export async function fetchAiBilling({ days = 30, teamId, userEmail, unitTier, unitId } = {}) {
  const params = new URLSearchParams({ days: String(days) });
  if (teamId) {params.set('team_id', teamId);}
  if (userEmail) {params.set(USER_EMAIL, userEmail);}
  if (unitTier != null) {params.set('unit_tier', String(unitTier));}
  if (unitId) {params.set('unit_id', unitId);}
  try {
    return await apiGet(`/discovery/ai-billing?${params.toString()}`);
  } catch (err) {
    logger.warn('[discovery] ai-billing failed', err);
    return {
      warehouse_available: false, days,
      total_usd: 0, avg_cost_per_day: 0, components: [],
      billing_by_tool: [], billing_by_subtype: [], billing_by_model: [],
      claude_over_time: [], claude_by_product: [], github_over_time: [], rates: [],
    };
  }
}

// ── AI Billing rate sheet (seat license prices, single source of truth) ─
// GET is readable by any signed-in user; the whole billing engine prices
// the license half from these figures. saveBillingRates is admin-only and
// updates the prices the Org Dashboard → AI Billing editor exposes.
export const DEFAULT_BILLING_RATES = [
  { tool: 'claude', tier: 'standard', label: 'Claude (standard)', seatPriceUsdMonth: 30 },
  { tool: 'claude', tier: 'premium', label: 'Claude (premium)', seatPriceUsdMonth: 150 },
  // Anthropic's usage-based Enterprise plan: a single seat + per-token usage.
  // Applies on/after the usage-based cutover (AIFIN_CLAUDE_USAGE_BASED_SINCE).
  // $20/seat is Anthropic's published Enterprise list price (negotiated in practice).
  { tool: 'claude', tier: 'enterprise', label: 'Claude (Enterprise seat)', seatPriceUsdMonth: 20 },
  { tool: 'github_copilot', tier: 'standard', label: 'GitHub Copilot Enterprise', seatPriceUsdMonth: 39 },
  { tool: 'github_copilot', tier: 'business', label: 'GitHub Copilot Business', seatPriceUsdMonth: 19 },
  { tool: 'microsoft_copilot', tier: 'standard', label: 'Microsoft 365 Copilot', seatPriceUsdMonth: 30 },
  // Mirrors the server default (AIFIN_CURSOR_SEAT_USD_MONTH). OpenAI API has
  // no seat, so it has no rate row.
  { tool: 'cursor', tier: 'standard', label: 'Cursor Teams seat', seatPriceUsdMonth: 30 },
];

export async function fetchBillingRates() {
  try {
    const r = await apiGet('/discovery/billing-rates');
    return r?.rates || DEFAULT_BILLING_RATES;
  } catch (err) {
    logger.warn('[discovery] billing-rates GET failed', err);
    return DEFAULT_BILLING_RATES;
  }
}

export async function saveBillingRates(rates) {
  const r = await apiSend('PUT', '/discovery/billing-rates', { rates });
  return r?.rates || rates;
}

// Return the model family for a model key in the FINANCE_MODELS catalogue
// ('haiku' | 'sonnet' | 'opus'), or null if it's unrecognised.
export function modelFamily(modelKey) {
  if (!modelKey || typeof modelKey !== 'string') {return null;}
  const k = modelKey.toLowerCase();
  for (const fam of MODEL_FAMILIES) {
    if (k.includes(fam)) {return fam;}
  }
  return null;
}

// Cheapest input rate per family from FINANCE_MODELS — used as the
// representative $/Mtok when redistributing tokens to the ideal mix.
// Claude Code is input-token weighted, and within a family the
// output/input ratio is uniform (output = 5× input), so the input rate
// captures the family's price level for cross-family comparisons.
export function familyInputRate(family) {
  const candidates = MODELS.filter(m => modelFamily(m.key) === family);
  if (!candidates.length) {return 0;}
  return Math.min(...candidates.map(m => m.input || 0));
}

// Query-string builder guards, factored out so an endpoint with many optional
// filters doesn't rack up one `if` per param on its own cyclomatic-complexity
// count. `params.set` USVString-converts its value the same way `String(x)`
// would, so wrapping (or not) here changes nothing observable.
function setIfTruthy(params, key, value) { if (value) {params.set(key, String(value));} }
function setIfNotNullish(params, key, value) { if (value != null) {params.set(key, String(value));} }
function setIfNonEmptyList(params, key, list, joiner = ',') { if (list && list.length) {params.set(key, list.join(joiner));} }

// ── Iteration insights — Rally items × AI usage per iteration ───────────
export async function fetchIterationInsights({ iterationName, teamId, teamIds, userEmail, limit, unitTier, unitId, source, days } = {}) {
  const params = new URLSearchParams();
  setIfTruthy(params, 'iteration_name', iterationName);
  setIfTruthy(params, 'team_id', teamId);
  setIfNonEmptyList(params, 'team_ids', teamIds);
  setIfTruthy(params, USER_EMAIL, userEmail);
  setIfNotNullish(params, 'unit_tier', unitTier);
  setIfTruthy(params, 'unit_id', unitId);
  setIfTruthy(params, 'source', source);
  setIfTruthy(params, 'days', days);
  setIfTruthy(params, 'limit', limit);
  const qs = params.toString();
  const path = withQuery('/discovery/iteration-insights', qs);
  return apiGet(path);
}

/** Time-bucketed delivery (story points) vs AI usage series for the
 *  Productivity tab's Velocity & AI-usage chart. `granularity` is one of
 *  day | week | month | iteration. See GET /discovery/productivity-series. */
export async function fetchProductivitySeries({ teamId, teamIds, userEmail, unitTier, unitId, source, granularity, days } = {}) {
  const params = new URLSearchParams();
  setIfTruthy(params, 'team_id', teamId);
  setIfNonEmptyList(params, 'team_ids', teamIds);
  setIfTruthy(params, USER_EMAIL, userEmail);
  setIfNotNullish(params, 'unit_tier', unitTier);
  setIfTruthy(params, 'unit_id', unitId);
  setIfTruthy(params, 'source', source);
  setIfTruthy(params, 'granularity', granularity);
  setIfTruthy(params, 'days', days);
  const qs = params.toString();
  return apiGet(withQuery('/discovery/productivity-series', qs));
}

/** Engineer AI-adoption groups (light / medium / heavy by AI-active days over a
 *  fixed 30-day window) with each group's average velocity (story points per
 *  iteration) and average AI-generated LoC. Backs the two adoption bar charts
 *  on the Tier / Org dashboards' Productivity tab.
 *  See GET /discovery/ai-adoption-groups. */
export async function fetchAiAdoptionGroups({ teamId, teamIds, userEmail, unitTier, unitId, source } = {}) {
  const params = new URLSearchParams();
  if (teamId) {params.set('team_id', teamId);}
  if (teamIds && teamIds.length) {params.set('team_ids', teamIds.join(','));}
  if (userEmail) {params.set(USER_EMAIL, userEmail);}
  if (unitTier != null) {params.set('unit_tier', String(unitTier));}
  if (unitId) {params.set('unit_id', unitId);}
  if (source) {params.set('source', source);}
  const qs = params.toString();
  return apiGet(withQuery('/discovery/ai-adoption-groups', qs));
}

/** AI-tool adoption breakdown for the dashboards' "AI Adoption" tab: three
 *  distributions over the people in scope (any-tool share; engineer-titled
 *  Claude Code vs GitHub Copilot; non-engineer Claude.ai vs MS 365 Copilot),
 *  measured over all available history. See GET /discovery/ai-adoption. */
export async function fetchAiAdoption({ teamId, teamIds, userEmail, unitTier, unitId } = {}) {
  const params = new URLSearchParams();
  if (teamId) {params.set('team_id', teamId);}
  if (teamIds && teamIds.length) {params.set('team_ids', teamIds.join(','));}
  if (userEmail) {params.set(USER_EMAIL, userEmail);}
  if (unitTier != null) {params.set('unit_tier', String(unitTier));}
  if (unitId) {params.set('unit_id', unitId);}
  const qs = params.toString();
  try {
    return await apiGet(withQuery('/discovery/ai-adoption', qs));
  } catch (err) {
    logger.warn('[discovery] ai-adoption failed', err);
    return {
      warehouse_available: false, window: 'all', memberCount: 0,
      anyTool: { withAny: 0, withoutAny: 0 },
      engineers: { total: 0, claudeOnly: 0, copilotOnly: 0, both: 0, none: 0 },
      nonEngineers: { total: 0, claudeOnly: 0, msCopilotOnly: 0, both: 0, none: 0 },
      people: [],
    };
  }
}

/** Per-(iteration, owner email) AI cost + outcome rows for the Productivity
 *  tab's "Iteration metrics" table. See GET /discovery/delivery-metrics?grain=iteration.
 *  `source` selects the project-tracking backend: 'rally' (default) or 'jira'. */
export async function fetchIterationMetrics({ teamId, teamIds, userEmail, limit, unitTier, unitId, source } = {}) {
  const params = new URLSearchParams();
  if (teamId) {params.set('team_id', teamId);}
  if (teamIds && teamIds.length) {params.set('team_ids', teamIds.join(','));}
  if (userEmail) {params.set(USER_EMAIL, userEmail);}
  if (unitTier != null) {params.set('unit_tier', String(unitTier));}
  if (unitId) {params.set('unit_id', unitId);}
  if (source) {params.set('source', source);}
  if (limit) {params.set('limit', String(limit));}
  const qs = params.toString();
  return apiGet(withQuery('/discovery/delivery-metrics?grain=iteration', qs, '&'));
}

/** Per-(task, owner email) AI cost + outcome rows for the Productivity tab's
 *  "Task metrics" table. See GET /discovery/delivery-metrics?grain=task.
 *  `source` selects the project-tracking backend: 'rally' (default) or 'jira'. */
export async function fetchTaskMetrics({ teamId, teamIds, userEmail, limit, unitTier, unitId, source } = {}) {
  const params = new URLSearchParams();
  if (teamId) {params.set('team_id', teamId);}
  if (teamIds && teamIds.length) {params.set('team_ids', teamIds.join(','));}
  if (userEmail) {params.set(USER_EMAIL, userEmail);}
  if (unitTier != null) {params.set('unit_tier', String(unitTier));}
  if (unitId) {params.set('unit_id', unitId);}
  if (source) {params.set('source', source);}
  if (limit) {params.set('limit', String(limit));}
  const qs = params.toString();
  return apiGet(withQuery('/discovery/delivery-metrics?grain=task', qs, '&'));
}

// Forward-looking workload: one row per upcoming/in-flight (task, owner) whose
// sprint window overlaps [today, today + horizonDays]. Backs the Budget tab's
// upcoming-tasks Gantt + per-member workload insights (no AI-cost block — this
// is planned work, not past spend). Each row: { email, name, teamId, teamName,
// taskId, taskName, taskType, iteration, scheduleState, startDate, endDate,
// storyPoints, estimatedHours, remainingHours, started }.
export async function fetchUpcomingTasks({ teamId, teamIds, userEmail, unitTier, unitId, source, horizonDays, startDate, endDate } = {}) {
  const params = new URLSearchParams();
  setIfTruthy(params, 'team_id', teamId);
  setIfNonEmptyList(params, 'team_ids', teamIds);
  setIfTruthy(params, USER_EMAIL, userEmail);
  setIfNotNullish(params, 'unit_tier', unitTier);
  setIfTruthy(params, 'unit_id', unitId);
  setIfTruthy(params, 'source', source);
  setIfTruthy(params, 'horizon_days', horizonDays);
  // ISO date lower bound — widens the window into the past so the whole period
  // is covered (not just forward-looking work). Omitted ⇒ backend uses today.
  setIfTruthy(params, 'start_date', startDate);
  // ISO date upper bound — when set, the window is [start_date, end_date], so a
  // fully PAST period (e.g. last month) can be requested for the prior-period
  // workload column.
  setIfTruthy(params, 'end_date', endDate);
  const qs = params.toString();
  return apiGet(withQuery('/discovery/upcoming-tasks', qs));
}

// Historical fully-loaded AI cost rate ($/story-point) for a unit, each of its
// children (or members at the lowest tier), and the viewer's whole scope (org).
// Backs the Budget tab's "AI Cost Estimation" column: estimate = rate × upcoming
// story points, using a child's own rate then parent/org fallback. Rates are
// null where there is no completed-task history. Omit tier/unit for the org.
export async function fetchCostRate({ tierLevel, unitId, source } = {}) {
  const params = new URLSearchParams();
  if (tierLevel != null) {params.set('tier_level', String(tierLevel));}
  if (unitId) {params.set('unit_id', unitId);}
  if (source) {params.set('source', source);}
  const qs = params.toString();
  return apiGet(withQuery('/discovery/cost-rate', qs));
}

// ══════════════════════════════════════════════════════════════════════
// Public roster API (sync — reads from cache; empty until loaded)
// ══════════════════════════════════════════════════════════════════════

export function listTeams()    { return _state.teams.map(t => ({ ...t, members: [...(t.members || [])] })); }
export function listMembers()  { return _state.members.map(m => ({ ...m })); }
export function listProjects() { return _state.projects.map(p => ({ ...p })); }
export function listStories()  { return _state.stories.map(s => ({ ...s })); }

/** Resolve the "current user" by email.
 *
 *  Resolution order:
 *    1. Roster match (bronze__rally / github / claude-code surfaced the
 *       address) — return the roster row with matched=true.
 *    2. Usage match — the warehouse has Claude Code rows keyed by this
 *       email but the roster syncs haven't linked the address yet.
 *       Synthesize a virtual member so the personal page still shows
 *       the user's own usage / cost / quota and getMemberUsage hits
 *       the same _state.usageByEmail bucket.
 *    3. Otherwise return a matched=false placeholder so MyAIFinance can
 *       render an explicit "not mapped" empty state instead of silently
 *       falling back to members[0]. */
export function resolveCurrentMember(email) {
  const lower = email ? String(email).toLowerCase() : '';
  if (lower) {
    const m = _state.members.find(x => x.email && x.email.toLowerCase() === lower);
    if (m) {return { ...m, matched: true };}
    const hasUsage = _state.usageByEmail && Object.prototype.hasOwnProperty.call(_state.usageByEmail, lower)
      && Object.keys(_state.usageByEmail[lower] || {}).length > 0;
    if (hasUsage) {
      return {
        id: `email:${lower}`,
        name: lower.split('@')[0],
        email: lower,
        role: '',
        matched: true,
        synthetic: true,
      };
    }
  }
  const fallbackName = email ? String(email).split('@')[0] : 'You';
  return { id: '', name: fallbackName, email: email || '', role: '', matched: false };
}

export function listTeamsForMember(memberId) {
  if (!memberId) {return [];}
  return _state.teams.filter(t => (t.members || []).includes(memberId));
}

// Default monthly quotas (tokens / cost units) for members and teams.
const AI_MEMBER_TOKEN_QUOTA = 600_000;
const MEMBER_TOKEN_QUOTA = 200_000;
const AI_MEMBER_COST_QUOTA = 120;
const MEMBER_COST_QUOTA = 40;
const TEAM_TOKEN_QUOTA_PER_MEMBER = 300_000;
const TEAM_COST_QUOTA_PER_MEMBER = 60;

export function getMemberQuota(memberId) {
  const m = _state.members.find(x => x.id === memberId);
  const isAI = m && /AI/i.test(m.role || '');
  return {
    tokenQuota: isAI ? AI_MEMBER_TOKEN_QUOTA : MEMBER_TOKEN_QUOTA,
    costQuota: isAI ? AI_MEMBER_COST_QUOTA : MEMBER_COST_QUOTA,
    period: 'month',
  };
}

export function getTeamQuota(teamId) {
  const t = _state.teams.find(x => x.id === teamId);
  if (!t) {return { tokenQuota: 0, costQuota: 0, period: 'month' };}
  const size = (t.members || []).length || 1;
  return {
    tokenQuota: size * TEAM_TOKEN_QUOTA_PER_MEMBER,
    costQuota: size * TEAM_COST_QUOTA_PER_MEMBER,
    period: 'month',
  };
}

// ══════════════════════════════════════════════════════════════════════
// Usage accessors — real data only (empty rows when no warehouse data)
// ══════════════════════════════════════════════════════════════════════

function realMemberSeries(member, days) {
  const email = (member?.email || '').toLowerCase();
  const byDay = _state.usageByEmail[email] || {};
  const dates = dateRange(days);
  return dates.map(d => {
    const row = byDay[d];
    if (!row) {return emptyDailyRow(d);}
    return { ...row };
  });
}

// ── Derivation memo (version-keyed) ───────────────────────────────────────
// The usage rollups below are PURE over the module cache _state: identical
// inputs at the same data version always produce the same output. Each is an
// O(members × days × metrics) walk (getMemberUsage fans out per member;
// mergeSeriesList folds per day), and they're called from several cards and on
// every re-render. Memoise by (fn, args) within one data version so repeated
// calls — multiple cards on a page, a tab revisit, a team switch and back, a
// parent re-render that didn't change scope — are free instead of re-walking.
// notify() bumps _state._tick whenever the cache changes; a new tick wipes the
// memo so results never go stale. Consumers treat returned values as read-only
// (the same contract as the shared _state arrays these are derived from).
let _deriveMemoTick = -1;
const _deriveMemo = new Map();
function _memoizeDerive(name, args, compute) {
  const tick = _state._tick || 0;
  if (tick !== _deriveMemoTick) {
    _deriveMemo.clear();
    _deriveMemoTick = tick;
  }
  const key = `${name}|${JSON.stringify(args)}`;
  if (_deriveMemo.has(key)) {return _deriveMemo.get(key);}
  const out = compute();
  _deriveMemo.set(key, out);
  return out;
}

export function getMemberUsage({ memberId, days = 30, projectId = null } = {}) {
  return _memoizeDerive('getMemberUsage', [memberId, days, projectId], () =>
    _computeMemberUsage({ memberId, days, projectId }));
}

function _computeMemberUsage({ memberId, days = 30, projectId = null }) {
  // Look the member up in the roster first; if the caller passed a
  // synthetic `email:<addr>` id (resolveCurrentMember's fallback for
  // users that are in usage but not on the roster), reconstruct a
  // virtual member from the email so realMemberSeries still hits the
  // right _state.usageByEmail bucket.
  let member = _state.members.find(m => m.id === memberId);
  if (!member && typeof memberId === 'string' && memberId.startsWith('email:')) {
    const email = memberId.slice('email:'.length);
    member = { id: memberId, name: email.split('@')[0], email, role: '' };
  }
  if (!member) {
    const empty = dateRange(days).map(d => emptyDailyRow(d));
    return { series: empty, total: sumSeries(empty) };
  }
  let series = realMemberSeries(member, days);
  if (projectId) {
    // Real data is not partitioned by project; scale by this member's
    // story share for the requested project so per-project rollups still
    // change when the user filters.
    const memberStoryCount = _state.stories.filter(s => s.teamId &&
      (_state.teams.find(t => t.id === s.teamId)?.members || []).includes(memberId)).length || 1;
    const projectStoryCount = _state.stories.filter(s => s.projectId === projectId &&
      (_state.teams.find(t => t.id === s.teamId)?.members || []).includes(memberId)).length;
    if (projectStoryCount > 0 && memberStoryCount > 0) {
      const share = projectStoryCount / memberStoryCount;
      series = series.map(r => ({
        ...r,
        interactions: Math.round((r.interactions || 0) * share),
        inputTokens: Math.round((r.inputTokens || 0) * share),
        cacheTokens: Math.round((r.cacheTokens || 0) * share),
        outputTokens: Math.round((r.outputTokens || 0) * share),
        totalTokens: Math.round((r.totalTokens || 0) * share),
        cost: Number(((r.cost || 0) * share).toFixed(4)),
        perModel: Object.fromEntries(Object.entries(r.perModel || {}).map(([k, v]) => [k, Math.round(v * share)])),
        perModelCost: Object.fromEntries(Object.entries(r.perModelCost || {}).map(([k, v]) => [k, Number((v * share).toFixed(4))])),
      }));
    } else {
      series = series.map(r => ({ ...emptyDailyRow(r.date) }));
    }
  }
  return { series, total: sumSeries(series) };
}

function mergeSeriesList(seriesList) {
  if (seriesList.length === 0) {return [];}
  const dates = seriesList[0].map(r => r.date);
  return dates.map((d, i) => {
    const row = emptyDailyRow(d);
    for (const s of seriesList) {
      const r = s[i];
      if (!r) {continue;}
      addRowInto(row, r);
      mergeModelMapsInto(row, r);
    }
    return roundMoneyFields(row, 4);
  });
}

// Sum the usage series of `memberIds` over the window, on the client — the path
// the team / unit / org accessors take until their server aggregate lands.
//
// With no per-member row loaded at all, every member's series is zeros, so the
// sum is known without walking anyone. That is the org page's normal state: it
// declares usageScope 'org', reads usage-summary instead and leaves usageByEmail
// empty. The walk was not free there. getMemberUsage looks each member up in the
// roster, so one team cost O(members x roster), and every usage-summary that
// landed bumped the tick, wiped the memo and paid it again for each team still
// pending. On the 5,000-person seeded org that held the main thread for 98% of
// the minute after AI Usage was opened, and a click on the next tab took over
// 10s to land.
function _mergeMemberUsage(memberIds, days) {
  if (!memberIds.length) {return { series: [], total: sumSeries([]) };}
  if (!Object.keys(_state.usageByEmail).length) {return _zeroUsage(days);}
  const merged = mergeSeriesList(memberIds.map(id => getMemberUsage({ memberId: id, days }).series));
  return { series: merged, total: sumSeries(merged) };
}

// The all-zero window, built once per (days, first day) rather than once per
// team per notification: it is what every org-page team reads until its own
// summary lands, and handing back the SAME object is what lets the team table
// below skip the rows that did not change (see _teamRow). Read-only, like every
// value these accessors return.
const _zeroUsageCache = new Map();
function _zeroUsage(days) {
  const dates = dateRange(days);
  const key = `${days}|${dates[0] || ''}`;
  let hit = _zeroUsageCache.get(key);
  if (!hit) {
    const series = dates.map(d => emptyDailyRow(d));
    hit = { series, total: sumSeries(series) };
    _zeroUsageCache.clear();
    _zeroUsageCache.set(key, hit);
  }
  return hit;
}

// One `{ team, total }` row per team, reused while the team's total is the same
// object. The org AI Usage table re-renders on every batch of per-team
// summaries; with stable rows antd only re-renders the rows whose team landed.
const _teamRowCache = new WeakMap();
function _teamRow(team, total) {
  const hit = _teamRowCache.get(team);
  if (hit && hit.total === total) {return hit;}
  const row = { team, total };
  _teamRowCache.set(team, row);
  return row;
}

export function getTeamUsage({ teamId, days = 30 }) {
  // Server aggregate when it has arrived; the client-side merge below is the
  // fallback for the first render and for a failed request. Both produce the
  // same numbers (TC-US-01) — the server one just does not need every member's
  // series in the browser to get there.
  const summary = _usageSummary({ days, teamId });
  if (summary && summary.series) {return { series: summary.series, total: summary.total };}
  return _memoizeDerive('getTeamUsage', [teamId, days], () => {
    const team = _state.teams.find(t => t.id === teamId);
    if (!team) {return { series: [], total: sumSeries([]) };}
    return _mergeMemberUsage(team.members || [], days);
  });
}

// Derive a human-readable name from a corporate email when the catalog
// has no real display name. Only "first.last" style locals are
// humanised ("thang.vannguyen2@…" → "Thang Vannguyen2"); synthetic or
// hash-style locals (e.g. "user_ec67c5e51ff0fafc@…") are left for the
// caller to fall back to the raw email, since title-casing a hash reads
// worse than the address itself.
function _humanizeEmailName(email) {
  if (!email || typeof email !== 'string') {return '';}
  const local = (email.split('@')[0] || '').trim();
  if (!local.includes('.')) {return '';}
  const parts = local.split(/[._]+/).filter(Boolean);
  if (!parts.length || parts.some(p => /\d{4,}/.test(p))) {return '';}
  return parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ').trim();
}

export function getTeamMemberUsage({ teamId, days = 30 }) {
  return _memoizeDerive('getTeamMemberUsage', [teamId, days], () =>
    _computeTeamMemberUsage({ teamId, days }));
}

// Only treat a single-token name as a handle when it ALSO looks handle-ish:
// it contains a digit / handle separator (. _ -), or it exactly echoes the
// email local-part. This preserves legitimate single-word mononyms. Shared by
// the team- and unit-scoped engineer-name resolvers below (identical rule).
function _looksLikeHandle(raw, email) {
  if (!raw || /\s/.test(raw)) {return false;}
  const emailLocalPart = (email || '').split('@')[0].trim();
  return /[._\-\d]/.test(raw) || (!!emailLocalPart && raw.toLowerCase() === emailLocalPart.toLowerCase());
}

// Fallback chain once a team member's catalog name has been rejected: a
// humanised email, then the raw email, then the original token (only if it
// wasn't a team echo), then a de-prefixed catalog id.
function _fallbackTeamMemberName(m, mid, raw, isTeamEcho) {
  const humanized = _humanizeEmailName(m.email);
  if (humanized) {return humanized;}
  if (m.email) {return m.email;}
  if (!isTeamEcho && raw) {return raw;}
  return typeof mid === 'string' ? mid.replace(/^(person|rally)-/, '') : String(mid);
}

// Engineer label resolution. The catalog `name` is coalesce(pagerduty_name,
// rally_name, servicenow_name, github_enterprise_name) — and
// github_enterprise_name is the git commit author's `user.name`, which is
// frequently a handle like "404DNF" rather than a person's real name. We
// therefore reject: empty names, names that echo the team's own name
// (group-as-person rows), and single-token handles/logins — falling back
// through _fallbackTeamMemberName so each row identifies a real engineer.
function _resolveTeamMemberDisplayName(m, mid, teamNameLower) {
  const raw = (m.name || '').trim();
  const isTeamEcho = Boolean(raw) && raw.toLowerCase() === teamNameLower;
  const isHandle = _looksLikeHandle(raw, m.email);
  if (!raw || isTeamEcho || isHandle) {return _fallbackTeamMemberName(m, mid, raw, isTeamEcho);}
  return raw;
}

function _computeTeamMemberUsage({ teamId, days = 30 }) {
  const team = _state.teams.find(t => t.id === teamId);
  if (!team) {return [];}
  const teamNameLower = (team.name || '').trim().toLowerCase();
  return (team.members || []).map(mid => {
    const m = _state.members.find(x => x.id === mid) || { id: mid, name: '', email: '', role: '' };
    const u = getMemberUsage({ memberId: mid, days });
    const name = _resolveTeamMemberDisplayName(m, mid, teamNameLower);
    // Spread usage first so member identity (id/email/role) and the
    // resolved name always win over anything in the rollup.
    return { ...u.total, id: m.id, email: m.email, role: m.role, name };
  }).sort((a, b) => b.cost - a.cost);
}

// ── Org-Structure unit usage (an explicit set of member emails) ───────────
// Powers the AI Usage tab on the dynamic tier dashboards: aggregates the same
// per-member usage feed over the unit's rolled-up emails, so the whole legacy
// AI Usage view (KPIs, charts, tool containers, breakdown) renders scoped to
// the unit with no roster-team dependency.
export function getUnitUsage({ emails = [], days = 30, unitTier = null, unitId = null } = {}) {
  // `unitTier`/`unitId` let this reach the server aggregate. They are optional
  // because the original signature took only `emails`, and an email LIST cannot
  // be the server's scope — a 1,264-member unit does not fit in a query string.
  // A caller that has the unit identity should pass it; one that only has emails
  // still works, on the client-side path.
  if (unitTier != null && unitId) {
    const summary = _usageSummary({ days, unitTier, unitId });
    if (summary && summary.series) {return { series: summary.series, total: summary.total };}
  }
  return _memoizeDerive('getUnitUsage', [emails, days], () =>
    _mergeMemberUsage(_membersInScope({ emails }).map(m => m.id), days));
}

// Per-engineer usage rows for a unit's members (the unit-scoped analog of
// getTeamMemberUsage — same row shape so the breakdown table reuses it).
export function getUnitMemberUsage({ emails = [], days = 30 } = {}) {
  return _memoizeDerive('getUnitMemberUsage', [emails, days], () =>
    _computeUnitMemberUsage({ emails, days }));
}

// Fallback chain once a unit member's catalog name has been rejected as
// handle-ish: a humanised email, then the raw email, then a de-prefixed
// catalog id (the unit-scoped rows can be `email:`-synthesised, unlike the
// team-scoped ones, hence the extra prefix stripped below).
function _fallbackUnitMemberName(m) {
  const humanized = _humanizeEmailName(m.email);
  if (humanized) {return humanized;}
  if (m.email) {return m.email;}
  return typeof m.id === 'string' ? m.id.replace(/^(person|rally|email:)/, '') : String(m.id);
}

// Mirror getTeamMemberUsage's engineer-name resolution (see
// _resolveTeamMemberDisplayName): keep single-token mononyms, only drop names
// that look handle-ish. Unit scope has no team to echo-check against.
function _resolveUnitMemberDisplayName(m) {
  const raw = (m.name || '').trim();
  const isHandle = _looksLikeHandle(raw, m.email);
  if (raw && !isHandle) {return raw;}
  return _fallbackUnitMemberName(m);
}

function _computeUnitMemberUsage({ emails = [], days = 30 } = {}) {
  const members = _membersInScope({ emails });
  return members.map((m) => {
    const u = getMemberUsage({ memberId: m.id, days });
    const name = _resolveUnitMemberDisplayName(m);
    return { ...u.total, id: m.id, email: m.email, role: m.role || '', name };
  }).sort((a, b) => b.cost - a.cost);
}

export function getOrgUsage({ days = 30 }) {
  // Org series/total come from the server; `byTeam` deliberately does not — it
  // is a per-team breakdown, so it keeps using getTeamUsage (which is itself
  // server-backed per team). Only the org-wide roll-up moves here.
  const summary = _usageSummary({ days });
  if (summary && summary.series) {
    const byTeam = _state.teams.map(t => ({ team: t, total: getTeamUsage({ teamId: t.id, days }).total }));
    return { series: summary.series, total: summary.total, byTeam };
  }
  return _memoizeDerive('getOrgUsage', [days], () => {
    // No roster teams (org-structure tenant) → aggregate the whole org from
    // per-email usage so the org view isn't empty.
    if (!_state.teams.length) {
      return { ..._mergeMemberUsage(_membersInScope({}).map(m => m.id), days), byTeam: [] };
    }
    const teamUsage = _state.teams.map(t => ({
      team: t, usage: getTeamUsage({ teamId: t.id, days }),
    }));
    const merged = mergeSeriesList(teamUsage.map(x => x.usage.series));
    const total = sumSeries(merged);
    return {
      series: merged,
      total,
      byTeam: teamUsage.map(t => ({ team: t.team, total: t.usage.total })),
    };
  });
}

// Usage aggregated over an arbitrary set of teams (or all teams when
// `teamIds` is empty/null) — the org-scoped analog of getTeamUsage that the
// AI Usage tab uses so the Org View's team multi-select narrows the view.
export function getTeamsUsage({ teamIds = null, days = 30 } = {}) {
  return _memoizeDerive('getTeamsUsage', [teamIds, days], () => {
    const allTeams = !(teamIds && teamIds.length);
    const wanted = allTeams ? _state.teams : _state.teams.filter(t => teamIds.includes(t.id));
    // "All" requested but no roster teams (org-structure tenant) → aggregate the
    // whole org straight from per-email usage so the org AI Usage view isn't empty.
    if (!wanted.length && allTeams) {
      return { ..._mergeMemberUsage(_membersInScope({}).map(m => m.id), days), byTeam: [] };
    }
    const teamUsage = wanted.map(t => ({ team: t, usage: getTeamUsage({ teamId: t.id, days }) }));
    const byTeam = teamUsage.map(t => _teamRow(t.team, t.usage.total));
    // Every team: the org-wide day series is the org summary the page already
    // loaded (loadOrgUsage), exactly as getOrgUsage reads it. Summing the
    // per-team summaries instead made the org's two "over time" charts depend on
    // one request per team (123 on the seeded org, 4 at a time, ~40s): the
    // charts drew a partial sum, then grew and re-animated every 300ms as each
    // team landed, for the whole minute after the tab opened. A person sits in
    // exactly one roster team (their deepest unit), so the two sums are the
    // same; the org summary also counts usage the roster has not placed on a
    // team yet ("Unassigned"), as the tab's other org-wide cards already do.
    // The SAME series object comes back on every call, which is what lets the
    // charts skip re-drawing on unrelated cache notifications.
    if (allTeams) {
      const org = _usageSummary({ days });
      if (org && org.series) {return { series: org.series, total: org.total, byTeam };}
    }
    const merged = mergeSeriesList(teamUsage.map(x => x.usage.series));
    return { series: merged, total: sumSeries(merged), byTeam };
  });
}

// Resolve the roster members in scope for an active-usage rollup. Mirrors
// resolveCurrentMember's fallback: an individual email with usage but no
// roster row still resolves to a virtual member so its daily rows are read.
// Org-Structure unit scope: an explicit set of member emails (resolved
// warehouse-side). Match roster members by email; synthesise a virtual
// member for any email with usage but no roster row.
function _membersFromEmailList(emails) {
  const set = new Set(emails.map(e => String(e).toLowerCase()).filter(Boolean));
  const found = _state.members.filter(m => m.email && set.has(m.email.toLowerCase()));
  const seen = new Set(found.map(m => m.email.toLowerCase()));
  const virtual = [...set].filter(e => !seen.has(e)).map(e => ({ id: `email:${e}`, email: e }));
  return [...found, ...virtual];
}

function _memberFromSingleEmail(email) {
  const lower = String(email).toLowerCase();
  const m = _state.members.find(x => x.email && x.email.toLowerCase() === lower);
  return m ? [m] : [{ id: `email:${lower}`, email: lower }];
}

function _membersFromTeamId(teamId) {
  const t = _state.teams.find(x => x.id === teamId);
  const ids = t ? (t.members || []) : [];
  return ids.map(id => _state.members.find(m => m.id === id)).filter(Boolean);
}

function _membersFromTeamIds(teamIds) {
  const idset = new Set();
  _state.teams.filter(t => teamIds.includes(t.id))
    .forEach(t => (t.members || []).forEach(id => idset.add(id)));
  return [...idset].map(id => _state.members.find(m => m.id === id)).filter(Boolean);
}

// "All" scope: the legacy roster. Org-structure tenants have no roster teams
// (members live in the org tree), so fall back to every person present in the
// usage feed — otherwise org-wide views aggregate nothing.
function _allMembersFallback() {
  if (_state.members.length) {return _state.members;}
  return Object.keys(_state.usageByEmail || {}).map(e => ({ id: `email:${e}`, email: e }));
}

function _membersInScope({ email = null, teamId = null, teamIds = null, emails = null } = {}) {
  if (emails && emails.length) {return _membersFromEmailList(emails);}
  if (email) {return _memberFromSingleEmail(email);}
  if (teamId) {return _membersFromTeamId(teamId);}
  if (teamIds && teamIds.length) {return _membersFromTeamIds(teamIds);}
  return _allMembersFallback();
}

// Per-tool "active" signals for a single day's row. A Claude-active day has
// chat/agent interactions, Claude tokens or Claude Code commits; a
// Copilot-active day has Copilot interactions or AI-credit consumption.
function _claudeActive(row) {
  if (!row) {return false;}
  const hasInteractions = (row.interactions || 0) > 0;
  const hasTokens = ((row.inputTokens || 0) + (row.outputTokens || 0)) > 0;
  const hasCommits = (row.ccCommits || 0) > 0;
  return hasInteractions || hasTokens || hasCommits;
}
function _copilotActive(row) {
  if (!row) {return false;}
  return (row.copilotInteractions || 0) > 0 || (row.copilotAiCredits || 0) > 0;
}
// A Cursor-active day has a request, a token, an accepted AI line or an
// accepted Tab completion; an OpenAI API-active day has a request or a
// token. Same rule as core/cursor_codex_usage.py cursor_active / codex_active.
function _cursorActive(row) {
  if (!row) {return false;}
  return ['cursorRequests', 'cursorTokens', 'cursorLinesAdded', 'cursorTabsAccepted']
    .some(f => (row[f] || 0) > 0);
}
function _codexActive(row) {
  if (!row) {return false;}
  return (row.codexRequests || 0) > 0 || (row.codexTokens || 0) > 0;
}
// Per-tool active flags for one day's row, in series order.
function _toolActivity(row) {
  return [_claudeActive(row), _copilotActive(row), _cursorActive(row), _codexActive(row)];
}
// A day is "active" overall when any tool was used.
function _rowIsActive(row) {
  return _toolActivity(row).some(Boolean);
}

// Active-users-over-time series for the AI Usage tab. For an individual scope
// (`email`) totalUsers is 1 and activeUsers is 0/1 per day, so the series is a
// month-style "active days" timeline; for team / org scopes it is the count of
// distinct engineers who touched an AI tool each day (DAU). `totalActiveDays`
// is the number of days in the window with ≥1 active user.
export function getActiveUsersOverTime({ email = null, teamId = null, teamIds = null, emails = null, days = 30 } = {}) {
  const members = _membersInScope({ email, teamId, teamIds, emails });
  const dates = dateRange(days);
  // With no per-member row loaded nobody can be counted active, so skip the
  // members x days walk: on the org page (which loads none) it was 50ms per
  // render on the seeded org, and that page re-renders once per team summary.
  const counted = Object.keys(_state.usageByEmail).length ? members : [];
  const series = dates.map(d => {
    let active = 0;
    // claude, copilot, cursor, codex — in _toolActivity order.
    const perTool = [0, 0, 0, 0];
    for (const m of counted) {
      const e = (m.email || '').toLowerCase();
      const row = e ? (_state.usageByEmail[e] || {})[d] : null;
      const flags = _toolActivity(row);
      flags.forEach((on, i) => { if (on) {perTool[i] += 1;} });
      if (flags.some(Boolean)) {active += 1;}
    }
    // The per-tool counts can overlap (a user active on several tools), so
    // they may sum to more than activeUsers — that is expected for a by-tool
    // breakdown.
    return {
      date: d, activeUsers: active,
      claudeActive: perTool[0], copilotActive: perTool[1],
      cursorActive: perTool[2], codexActive: perTool[3],
      totalUsers: members.length,
    };
  });
  return {
    series,
    totalUsers: members.length,
    totalActiveDays: series.filter(r => r.activeUsers > 0).length,
    peakActiveUsers: series.reduce((a, r) => Math.max(a, r.activeUsers), 0),
  };
}

/** Distinct people active per day, counted on the server (GET
 *  /discovery/active-users). The organization page loads no per-member usage,
 *  so getActiveUsersOverTime has nobody to count there (TD-16). */
export async function fetchActiveUsersOverTime({ days = 30, teamId, unitTier, unitId } = {}) {
  const params = new URLSearchParams({ days: String(days) });
  setIfTruthy(params, 'team_id', teamId);
  setIfNotNullish(params, 'unit_tier', unitTier);
  setIfTruthy(params, 'unit_id', unitId);
  return apiGet(withQuery('/discovery/active-users', params.toString()));
}

/** The server's per-day counts in the shape getActiveUsersOverTime returns, so
 *  the card draws either one. `totalUsers` is the scope's head-count. */
export function activeUsersFromServer(serverSeries, totalUsers) {
  const series = (serverSeries || []).map(r => ({
    date: r.date,
    activeUsers: r.activeUsers || 0,
    claudeActive: r.claudeActive || 0,
    copilotActive: r.copilotActive || 0,
    cursorActive: r.cursorActive || 0,
    codexActive: r.codexActive || 0,
    totalUsers,
  }));
  return {
    series,
    totalUsers,
    totalActiveDays: series.filter(r => r.activeUsers > 0).length,
    peakActiveUsers: series.reduce((a, r) => Math.max(a, r.activeUsers), 0),
  };
}

export function getProjectUsage({ days = 30 }) {
  return _state.projects.map(p => {
    const team = _state.teams.find(t => t.id === p.teamId);
    if (!team) {
      // A project with no mapped team has no roster to attribute spend to.
      // Returning the whole-org total here (the previous behaviour) made every
      // unmapped project double-count all org spend. Report zero + an
      // `unmapped` flag so callers can label it rather than inflate it.
      return { project: p, team: null, unmapped: true, ...sumSeries([]) };
    }
    const memberSeries = (team.members || []).map(mid =>
      getMemberUsage({ memberId: mid, days, projectId: p.id }).series
    );
    const merged = mergeSeriesList(memberSeries);
    const total = sumSeries(merged);
    return { project: p, team, ...total };
  }).sort((a, b) => b.cost - a.cost);
}

export function getStoryUsage({ days = 30 }) {
  const projects = getProjectUsage({ days });
  const projectById = Object.fromEntries(projects.map(p => [p.project.id, p]));
  return _state.stories.map(s => {
    const p = projectById[s.projectId];
    if (!p) {return null;}
    const siblings = _state.stories.filter(x => x.projectId === s.projectId).length || 1;
    // There is no per-story cost signal in the warehouse, so project spend is
    // split evenly across its stories. This is an ESTIMATE, not a measured
    // per-story figure — `costEstimated` lets the UI label it as such (the
    // file's "no synthetic fallback" contract is about fabricating spend that
    // doesn't exist; this is an explicit, flagged allocation of real spend).
    const cost = Number(((p.cost || 0) / siblings).toFixed(2));
    const totalTokens = Math.round((p.totalTokens || 0) / siblings);
    const interactions = Math.round((p.interactions || 0) / siblings);
    return { ...s, project: p.project, team: p.team, cost, totalTokens, interactions, costEstimated: siblings > 1 };
  }).filter(Boolean).sort((a, b) => b.cost - a.cost);
}

// ══════════════════════════════════════════════════════════════════════
// Productivity + ROI — real data only
// ══════════════════════════════════════════════════════════════════════

function realProductivitySeries(teamId, days) {
  const byDay = _state.productivityByTeam[teamId] || {};
  const dates = dateRange(days);
  return dates.map(d => byDay[d] || {
    date: d, velocity: 0, linesOfCode: 0, commits: 0,
    prsOpened: 0, prsMerged: 0, incidents: 0,
  });
}

// Per-day Claude-Code LoC/commits/PRs delta for a team, keyed by day. Pulled
// out of getTeamProductivity so its two nested loops (members × that
// member's usage days) don't push the enclosing function's if/if nesting
// past depth 3.
// A member's real-usage rows (keyed by day), or null when they can't be
// resolved to an emailed usage row at all.
function _memberRealUsageRows(mid) {
  const member = _state.members.find(m => m.id === mid);
  const email = (member?.email || '').toLowerCase();
  return email ? _state.usageByEmail[email] : null;
}

// Fold one real-usage day-row's Claude-Code activity into the three
// running per-day totals (mutates the accumulators in place).
function _addDailyClaudeCodeDelta(day, row, byDayLoc, byDayCommits, byDayPrs) {
  byDayLoc[day] = (byDayLoc[day] || 0) + (row.ccLinesAdded || 0) + (row.ccLinesRemoved || 0);
  byDayCommits[day] = (byDayCommits[day] || 0) + (row.ccCommits || 0);
  byDayPrs[day] = (byDayPrs[day] || 0) + (row.ccPullRequests || 0);
}

function _teamClaudeCodeDeltasByDay(team) {
  const byDayLoc = {};
  const byDayCommits = {};
  const byDayPrs = {};
  for (const mid of team.members || []) {
    const realRows = _memberRealUsageRows(mid);
    if (!realRows) {continue;}
    for (const [day, row] of Object.entries(realRows)) {
      _addDailyClaudeCodeDelta(day, row, byDayLoc, byDayCommits, byDayPrs);
    }
  }
  return { byDayLoc, byDayCommits, byDayPrs };
}

export function getTeamProductivity({ teamId, days = 30 }) {
  let series = realProductivitySeries(teamId, days);
  // Augment LoC + commits with Claude Code per-team rollup from real usage.
  const team = _state.realUsage ? _state.teams.find(t => t.id === teamId) : null;
  if (team) {
    const { byDayLoc, byDayCommits, byDayPrs } = _teamClaudeCodeDeltasByDay(team);
    series = series.map(r => ({
      ...r,
      // Sum the two sources rather than taking the larger: the realProductivity
      // series is the Rally/GitHub-attributed total, byDay* is the Claude-Code-
      // attributed delta. They measure different slices of the same day, so the
      // honest aggregate is r.* + byDay*. Math.max silently understated team
      // output whenever Rally/GitHub > Claude (the common case) and over-
      // attributed everything to AI when Claude rolled higher.
      linesOfCode: (r.linesOfCode || 0) + (byDayLoc[r.date] || 0),
      commits: (r.commits || 0) + (byDayCommits[r.date] || 0),
      prsOpened: (r.prsOpened || 0) + (byDayPrs[r.date] || 0),
    }));
  }
  return series;
}

// ══════════════════════════════════════════════════════════════════════
// Budgets + alerts — backed by tenant-api (Postgres)
// ══════════════════════════════════════════════════════════════════════

function labelForUnit(unitType, unitId) {
  if (unitType === 'team')    {return _state.teams.find(t => t.id === unitId)?.name || unitId;}
  if (unitType === 'user')    {return _state.members.find(m => m.id === unitId)?.name || unitId;}
  if (unitType === 'project') {return _state.projects.find(p => p.id === unitId)?.name || unitId;}
  if (unitType === 'story')   {return _state.stories.find(s => s.id === unitId)?.title || unitId;}
  if (unitType === 'org')     {return 'Organization';}
  return unitId;
}

function spendForUnit(unitType, unitId, days) {
  if (unitType === 'user') {return getMemberUsage({ memberId: unitId, days }).total;}
  if (unitType === 'team') {return getTeamUsage({ teamId: unitId, days }).total;}
  if (unitType === 'project') {
    const projects = getProjectUsage({ days });
    const p = projects.find(x => x.project.id === unitId);
    return p ? { cost: p.cost, totalTokens: p.totalTokens, interactions: p.interactions }
             : { cost: 0, totalTokens: 0, interactions: 0 };
  }
  if (unitType === 'story') {
    const stories = getStoryUsage({ days });
    const s = stories.find(x => x.id === unitId);
    return s ? { cost: s.cost, totalTokens: s.totalTokens, interactions: s.interactions }
             : { cost: 0, totalTokens: 0, interactions: 0 };
  }
  return { cost: 0, totalTokens: 0, interactions: 0 };
}

// Don't extrapolate a wild end-of-period projection from a 1–2 day sample.
// Exported so the budget panels gate their inline forecasts the same way.
export const MIN_FORECAST_DAYS = 3;

// #5: honour periodType (month / quarter / legacy rolling 'days') instead of
// treating periodDays as a trailing window that reaches into the prior period.
const ROLLING_WINDOW_DAYS = 30;

function _resolveBudgetPeriod(b) {
  const periodType = ['month', 'quarter', 'days'].includes(b.periodType) ? b.periodType : 'month';
  const win = periodWindow(periodType);
  const isLegacyDays = periodType === 'days';
  // Period-to-date spend: for calendar periods, spend since the period start
  // (≈ a trailing elapsedDays window anchored on today); for legacy 'days',
  // the rolling periodDays window.
  const windowDays = isLegacyDays ? (Number(b.periodDays) || ROLLING_WINDOW_DAYS) : win.elapsedDays;
  const totalDays = isLegacyDays ? (Number(b.periodDays) || ROLLING_WINDOW_DAYS) : win.totalDays;
  return { periodType, win, windowDays, totalDays };
}

function _pctOfLimit(value, limit) {
  return limit > 0 ? (value / limit) * 100 : 0;
}

// #5/#6: 'exceeded' only when actually over the cap now; otherwise pace-aware
// (on track to exceed?), guarded against a too-small sample so a day-1 spike
// can't fire a spurious "critical".
const BUDGET_WARNING_PCT = 75;
const BUDGET_CRITICAL_PCT = 90;

function _budgetStatusFromPcts(pct, forecastPct, reliable) {
  if (pct >= 100) {return 'exceeded';}
  if (reliable && forecastPct >= 100) {return 'critical';}
  if (reliable && forecastPct >= BUDGET_CRITICAL_PCT) {return 'warning';}
  if (pct >= BUDGET_CRITICAL_PCT) {return 'critical';}
  if (pct >= BUDGET_WARNING_PCT) {return 'warning';}
  return 'ok';
}

function withBudgetStatus(b) {
  const { periodType, win, windowDays, totalDays } = _resolveBudgetPeriod(b);
  const spend = spendForUnit(b.unitType, b.unitId, windowDays);

  // #6: utilization is honest period-to-date consumption (drives the bar);
  // it is naturally low early in a period and that is correct.
  const costPct = _pctOfLimit(spend.cost, b.costLimit);
  const tokenPct = _pctOfLimit(spend.totalTokens, b.tokenLimit);
  const pct = Math.max(costPct, tokenPct);

  // #4 + #13: ONE forecast method everywhere — to-date daily burn × period
  // length (no 7-day-from-scratch extrapolation, no per-panel variants).
  const elapsed = Math.max(1, windowDays);
  const reliable = periodType === 'days' || win.elapsedDays >= MIN_FORECAST_DAYS;
  const dailyRate = spend.cost / elapsed;
  const projectedCost = dailyRate * totalDays;
  const projectedPct = _pctOfLimit(projectedCost, b.costLimit);
  const projTokens = (spend.totalTokens / elapsed) * totalDays;
  const projTokenPct = _pctOfLimit(projTokens, b.tokenLimit);
  const forecastPct = Math.max(projectedPct, projTokenPct);

  const status = _budgetStatusFromPcts(pct, forecastPct, reliable);

  return {
    ...b,
    periodType,
    periodLabel: win.periodLabel,
    elapsedDays: win.elapsedDays,
    totalDays,
    spend: { cost: spend.cost, totalTokens: spend.totalTokens, interactions: spend.interactions || 0 },
    costPct: Number(costPct.toFixed(1)),
    tokenPct: Number(tokenPct.toFixed(1)),
    pct: Number(pct.toFixed(1)),
    projectedCost: Number(projectedCost.toFixed(2)),
    projectedPct: Number(projectedPct.toFixed(1)),
    forecastReliable: reliable,
    dailyRate: Number(dailyRate.toFixed(4)),
    status,
  };
}

/** Fetch all budgets and decorate with computed status. */
export async function listBudgets() {
  try {
    const resp = await apiGet('/discovery/budgets');
    const budgets = (resp.budgets || []).map(b => ({
      ...b,
      label: b.label || labelForUnit(b.unitType, b.unitId),
    }));
    return budgets.map(withBudgetStatus);
  } catch (err) {
    logger.warn('[discovery] listBudgets failed', err);
    return [];
  }
}


// ══════════════════════════════════════════════════════════════════════
// Budget period helpers — calendar month / quarter windows
// ══════════════════════════════════════════════════════════════════════

/**
 * Resolve a budgeting period to its trailing-day window so the existing
 * `days`-based billing endpoints can serve calendar-period spend.
 *
 *  • elapsedDays — days from the period start (1st of month / quarter) through
 *    today, inclusive. Pass this as `days` to fetchBillingOverTime/etc.
 *  • totalDays   — days in the whole period. Even-pace target + projection
 *    math frame against this.
 *
 * 'days' keeps the legacy rolling 30-day window.
 */
const MONTHS_PER_QUARTER = 3;

export function periodWindow(periodType = 'month', anchor = new Date()) {
  const d = new Date(anchor);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth(); // 0-11
  const day = d.getUTCDate();
  const DAY_MS = 86_400_000;
  if (periodType === 'quarter') {
    const qStartMonth = Math.floor(m / MONTHS_PER_QUARTER) * MONTHS_PER_QUARTER;
    const start = Date.UTC(y, qStartMonth, 1);
    const end = Date.UTC(y, qStartMonth + MONTHS_PER_QUARTER, 1); // first day of next quarter
    const totalDays = Math.round((end - start) / DAY_MS);
    const elapsedDays = Math.round((Date.UTC(y, m, day) - start) / DAY_MS) + 1;
    return { periodType, elapsedDays, totalDays, label: 'this quarter', periodLabel: `Q${qStartMonth / MONTHS_PER_QUARTER + 1} ${y}` };
  }
  if (periodType === 'year') {
    const start = Date.UTC(y, 0, 1);      // Jan 1
    const end = Date.UTC(y + 1, 0, 1);    // Jan 1 next year
    const totalDays = Math.round((end - start) / DAY_MS); // 365 / 366
    const elapsedDays = Math.round((Date.UTC(y, m, day) - start) / DAY_MS) + 1;
    return { periodType, elapsedDays, totalDays, label: 'this year', periodLabel: `FY${y}` };
  }
  if (periodType === 'days') {
    return { periodType, elapsedDays: 30, totalDays: 30, label: 'last 30 days', periodLabel: 'rolling 30d' };
  }
  // month (default)
  const monthStart = Date.UTC(y, m, 1);
  const monthEnd = Date.UTC(y, m + 1, 1);
  const monthTotalDays = Math.round((monthEnd - monthStart) / DAY_MS);
  const monthName = d.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
  return { periodType, elapsedDays: day, totalDays: monthTotalDays, label: 'this month', periodLabel: `${monthName} ${y}` };
}

// Build the burn-down chart's daily axis spanning the ENTIRE budget period
// (start → end), not just the cycle-to-date. The cumulative-spend line carries
// real values up to today and `null` afterwards (so the filled area stops at
// the present), while the even-pace target ramps to the full cap at period end
// and the cap line runs the whole width.
//
//   dates / dailyTotals — to-date per-day spend (any order, sparse-safe: keyed
//                         by date string, not index).
//   cap                 — the allocation/limit the target ramps to.
//   totalDays           — full length of the period (e.g. days in the month).
//   elapsedDays         — days from period start through today (inclusive).
//   anchor              — "today" (defaults to now); the axis ends `totalDays-1`
//                         days after the derived period start.
//
// For a rolling window pass totalDays === elapsedDays so the axis is simply the
// elapsed range with no future tail.
export function buildBurndownAxis({ dates = [], dailyTotals = [], cap = 0, totalDays, elapsedDays, anchor = new Date() } = {}) {
  const DAY_MS = 86_400_000;
  const total = Math.max(1, Math.round(Number(totalDays)) || (dates.length || 1));
  const elapsed = Math.max(1, Math.min(total, Math.round(Number(elapsedDays)) || total));
  const a = new Date(anchor);
  const todayMs = Date.UTC(a.getUTCFullYear(), a.getUTCMonth(), a.getUTCDate());
  const isoOfMs = (ms) => new Date(ms).toISOString().slice(0, 10);
  const startMs = todayMs - (elapsed - 1) * DAY_MS;
  const labels = [];
  for (let i = 0; i < total; i += 1) {labels.push(isoOfMs(startMs + i * DAY_MS));}
  // Spend keyed by date so sparse / out-of-order series still line up.
  const byDate = new Map();
  (dates || []).forEach((dt, i) => {
    const key = String(dt);
    byDate.set(key, (byDate.get(key) || 0) + (Number(dailyTotals[i]) || 0));
  });
  let acc = 0;
  const cumulative = labels.map((dt) => {
    if (Date.parse(dt) > todayMs) {return null;} // future day — no spend yet
    acc += byDate.get(dt) || 0;
    return acc;
  });
  const target = labels.map((_, i) => cap * ((i + 1) / total));
  const capLine = labels.map(() => cap);
  return { labels, cumulative, target, capLine };
}

// ── Shared dashboard time-range presets ────────────────────────────────
// Every Discovery dashboard exposes the same Time-range switcher. The
// option VALUES are stable string keys (not day counts) so MTD / YTD always
// render as their own buttons even when their resolved day-count coincides
// with a fixed preset — e.g. MTD on the 7th of the month would otherwise
// collide with "7d" and one button would disappear.
export const TIME_RANGE_OPTIONS = [
  { label: 'MTD', value: 'mtd', title: 'Month-to-date (1st of this month → today)' },
  { label: 'YTD', value: 'ytd', title: 'Year-to-date (Jan 1 → today)' },
  { label: '7d', value: '7' },
  { label: '14d', value: '14' },
  { label: '30d', value: '30' },
  { label: '90d', value: '90' },
];

// Resolve a Time-range key into the rolling-window day count every data
// consumer expects. MTD = day-of-month; YTD = day-of-year (Jan 1 → today).
const MS_PER_DAY = 86_400_000;

export function rangeToDays(range, anchor = new Date()) {
  const d = new Date(anchor);
  if (range === 'mtd') {return Math.max(1, d.getUTCDate());}
  if (range === 'ytd') {
    const startOfYear = Date.UTC(d.getUTCFullYear(), 0, 0); // Dec 31 last year → Jan 1 = 1
    return Math.max(1, Math.floor((Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) - startOfYear) / MS_PER_DAY));
  }
  const n = Number(range);
  return Number.isFinite(n) && n > 0 ? n : ROLLING_WINDOW_DAYS;
}

// ══════════════════════════════════════════════════════════════════════
// Budget roles + Power Users (budget managers) — tenant-api backed
// ══════════════════════════════════════════════════════════════════════

/** The signed-in user's AI-Finance budget tier + managed teams. */
export async function getBudgetRole() {
  try {
    const r = await apiGet('/discovery/me/budget-role');
    return {
      email: r.email || '',
      tier: r.tier || 'user',
      isAdmin: r.tier === 'admin',
      isPowerUser: r.tier === 'power_user',
      managedTeamIds: r.managedTeamIds || [],
      managedTeams: r.managedTeams || [],
    };
  } catch (err) {
    logger.warn('[discovery] getBudgetRole failed', err);
    return { email: '', tier: 'user', isAdmin: false, isPowerUser: false, managedTeamIds: [], managedTeams: [] };
  }
}

/** React hook — resolves the budget tier once on mount. */

export function useBudgetRole() {
  const [role, setRole] = useState({
    tier: null, isAdmin: false, isPowerUser: false,
    managedTeamIds: [], managedTeams: [], email: '', loading: true,
  });
  useEffect(() => {
    let cancelled = false;
    getBudgetRole().then(r => { if (!cancelled) {setRole({ ...r, loading: false });} });
    return () => { cancelled = true; };
  }, []);
  return role;
}

/** All Power-User → team assignments (admin only). */
export async function listPowerUsers() {
  try {
    const r = await apiGet('/discovery/power-users');
    return { powerUsers: r.powerUsers || [], assignments: r.assignments || [] };
  } catch (err) {
    logger.warn('[discovery] listPowerUsers failed', err);
    return { powerUsers: [], assignments: [] };
  }
}

/** Replace a user's managed-team set (admin only). teams: [{id,label}]. */
export async function setPowerUserTeams(email, teams) {
  return apiSend('POST', '/discovery/power-users', { email, teams });
}

/** Remove a single Power-User assignment by id (admin only). */
export async function removePowerUserAssignment(assignmentId) {
  return apiSend('DELETE', `/discovery/power-users/${encodeURIComponent(assignmentId)}`);
}

// ── Budget Alert Rules (Approach B) ───────────────────────────────────
// Warning/Critical threshold alerts on an Org-Structure cascade budget,
// keyed to a unit by (tierLevel, unitId). Delivered in-app + desktop.

/** All alert rules on a unit's budget + the unit's live spend/forecast metrics. */
export async function listBudgetAlertRules(tierLevel, unitId) {
  const qs = `tier_level=${encodeURIComponent(tierLevel)}&unit_id=${encodeURIComponent(unitId)}`;
  return apiGet(`/discovery/budget-alerts/rules?${qs}`);
}

/**
 * Create a new alert rule. payload: {tierLevel, unitId, name, basis
 * ('actual' | 'forecast' | 'anomaly'), warnPct, critPct, channels, recipients,
 * remind, watchMembers}. Recipients default server-side to the unit's leads —
 * a unit going over budget is the lead's problem, not its members'.
 */
export async function createBudgetAlertRule(payload) {
  return apiSend('POST', '/discovery/budget-alerts/rules', payload);
}

/** Update an existing alert rule (partial — only the provided fields change). */
export async function updateBudgetAlertRule(ruleId, payload) {
  return apiSend('PUT', `/discovery/budget-alerts/rules/${encodeURIComponent(ruleId)}`, payload);
}

/** Delete an alert rule. */
export async function deleteBudgetAlertRule(ruleId) {
  return apiSend('DELETE', `/discovery/budget-alerts/rules/${encodeURIComponent(ruleId)}`);
}

/** Send a one-off test notification for a rule to the current user. */
export async function testBudgetAlertRule(ruleId) {
  return apiSend('POST', `/discovery/budget-alerts/rules/${encodeURIComponent(ruleId)}/test`);
}

// ── Notifications (the sidebar bell feed) ─────────────────────────────

/** The current user's budget-alert notifications (newest first) + unread count. */
export async function listBudgetAlertNotifications({ unreadOnly = false, limit = 30 } = {}) {
  try {
    const qs = `unread_only=${unreadOnly ? 'true' : 'false'}&limit=${limit}`;
    return await apiGet(`/discovery/budget-alerts/notifications?${qs}`);
  } catch (err) {
    logger.warn('[discovery] listBudgetAlertNotifications failed', err);
    return { unread: 0, notifications: [] };
  }
}

/** Mark notifications read. Pass an array of ids, or nothing to mark all read. */
export async function markBudgetAlertNotificationsRead(ids) {
  return apiSend('POST', '/discovery/budget-alerts/notifications/mark-read',
    ids && ids.length ? { ids } : {});
}

/**
 * Close alerts as handled, or dismissed WITH a reason (the API rejects a
 * reasonless dismissal — a dismissal with no stated reason is indistinguishable
 * from an alert nobody looked at, which is what the digest is meant to expose).
 * Only the recipient of a row may close it.
 */
export async function handleBudgetAlertNotifications(ids, disposition, note) {
  return apiSend('POST', '/discovery/budget-alerts/notifications/handle',
    { ids: ids || [], disposition, ...(note ? { note } : {}) });
}

// ── Un-attributed spend (admin reconciliation) ────────────────────────

/**
 * Claude spend owned by no person (API keys / service accounts) and its share
 * of the total. Every per-person figure in AFM filters on an email, so this is
 * the money that is otherwise invisible — the gap between what AFM reports and
 * what the vendor invoices, and the obvious bypass if per-person limits are
 * ever switched on.
 */
export async function fetchUnattributedSpend(days = 30) {
  try {
    return await apiGet(`/discovery/unattributed-spend?days=${Number(days) || ROLLING_WINDOW_DAYS}`);
  } catch (err) {
    logger.warn('[discovery] fetchUnattributedSpend failed', err);
    return null;
  }
}
